import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { LogOut, Copy, ExternalLink, Settings, History, Code, CreditCard, LifeBuoy, Users, Globe, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import MiniSiteBuilder from "@/components/MiniSiteBuilder";
import BookingsAnalytics, { isRepeatBooking, countUniqueClients, ChannelBadge } from "@/components/BookingsAnalytics";
import ClientsList from "@/components/ClientsList";
import { Textarea } from "@/components/ui/textarea";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useTranslation } from "@/i18n/translations";
import Footer from "@/components/Footer";
import SupportTickets from "@/components/SupportTickets";
import { Whatsapp360Connect } from "@/components/Whatsapp360Connect";
import ShortLinkBlock from "@/components/ShortLinkBlock";
import { IdentIntegrationBlock } from "@/components/IdentIntegrationBlock";
import { VkIntegrationBlock } from "@/components/VkIntegrationBlock";
import GreenApiConnect from "@/components/GreenApiConnect";
import AnnouncementBar from "@/components/AnnouncementBar";
import ZernioInstagram from "@/components/ZernioInstagram";
import AiScheduleEditor from "@/components/AiScheduleEditor";
import DeleteAccountLink from "@/components/DeleteAccountLink";
import PhoneAgentCard from "@/components/PhoneAgentCard";
import VoiceReplySettings from "@/components/VoiceReplySettings";
import MarketingTab from "@/components/MarketingTab";
import PrivacySettings from "@/components/PrivacySettings";
import TildaMcpConnect from "@/components/TildaMcpConnect";
import AltegioConnect from "@/components/AltegioConnect";
import AnalyticsTab from "@/components/analytics/AnalyticsTab";
import ActiveAddonsList from "@/components/ActiveAddonsList";
import { addonCheckout } from "@/lib/addonCheckout";
import TeamManagement from "@/components/TeamManagement";
import OnboardingGuide from "@/components/OnboardingGuide";
import ReferralBanner from "@/components/ReferralBanner";


// Pricing constants - EUR for Stripe, RUB for Tinkoff
const PRICING = {
  eur: {
    monthly: {
      starter: 39,
      professional: 79,
      unlimited: 199
    },
    yearly: {
      starter: 390,  // 10 months price (2 months free)
      professional: 790,
      unlimited: 1990
    }
  },
  rub: {
    monthly: {
      starter: 2990,
      professional: 6990,
      unlimited: 14990
    },
    yearly: {
      starter: 29900,
      professional: 69900,
      unlimited: 149900
    }
  }
};

export default function Dashboard({ setIsAuthenticated, embedded = false, tab: tabProp }) {
  const { t, lang } = useTranslation();
  const navigate = useNavigate();
  const [salon, setSalon] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [settings, setSettings] = useState({
    telegram_chat_id: "",
    notification_emails: "",
    email_notifications: false,
    salon_info: "",
    job_reply: "",
    currency_symbol: "€",
    avg_ticket: "",
    calendar_enabled: true,
    operating_mode: "autopilot",
    manual_pause_hours: 6,
    ai_reply_delay_sec: 0,
    ai_schedule: null,
    voice_reply_mode: "off",
    voice_id: null,
    hide_chatity_branding: false,
    ai_intro_text: "",
    widget_settings: {
      primary_color: "#000000",
      secondary_color: "#FFFFFF",
      button_color: "#000000",
      welcome_message: "Здравствуйте! Чем могу вам помочь?",
      bot_name: "Ассистент",
      balloon_enabled: true,
      balloon_delay: 5,
      balloon_message: "Здравствуйте! Помогу записаться на услугу 👋",
      auto_open_enabled: false,
      auto_open_delay: 15
    }
  });
  const isEcom = settings.business_type === 'ecommerce';
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [boosterBalance, setBoosterBalance] = useState({ bonus_conversations_left: 0, bonus_conversations_purchased: 0 });
  const [boosterLoading, setBoosterLoading] = useState(false);
  const [addonsInfo, setAddonsInfo] = useState(null);
  const [boosterQty, setBoosterQty] = useState(1);
  const [addonLoading, setAddonLoading] = useState('');
  const [userCountry, setUserCountry] = useState(null);
  const [paymentSystem, setPaymentSystem] = useState('stripe'); // 'stripe' or 'tinkoff'
  const [promocodeInput, setPromocodeInput] = useState('');
  const [crmUrl, setCrmUrl] = useState('');
  const [crmPreview, setCrmPreview] = useState(null);
  const [crmLoading, setCrmLoading] = useState(false);
  const [crmSelected, setCrmSelected] = useState(new Set());
  const [crmImporting, setCrmImporting] = useState(false);

  const handleCrmPreview = async () => {
    if (!crmUrl.trim()) return;
    setCrmLoading(true);
    setCrmPreview(null);
    try {
      const r = await axios.post(`${API}/knowledge/import/preview`, { url: crmUrl.trim() });
      const svcs = r.data.services || [];
      setCrmPreview(svcs);
      setCrmSelected(new Set(svcs.map((_, i) => i)));
      if (svcs.length === 0) toast.error(t('settings.crmImportEmpty'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('settings.crmImportEmpty'));
    } finally {
      setCrmLoading(false);
    }
  };

  const handleCrmConfirm = async () => {
    const chosen = (crmPreview || []).filter((_, i) => crmSelected.has(i));
    if (chosen.length === 0) return;
    setCrmImporting(true);
    try {
      const r = await axios.post(`${API}/knowledge/import/confirm`, { services: chosen, url: crmUrl.trim() || undefined });
      setSettings((prev) => ({ ...prev, salon_info: r.data.salon_info }));
      setCrmPreview(null);
      toast.success(t('settings.crmImportSuccess', { count: r.data.imported }) || `Импортировано: ${r.data.imported}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setCrmImporting(false);
    }
  };
  const [promocodeApplied, setPromocodeApplied] = useState(null);
  const [promocodeError, setPromocodeError] = useState('');
  const [conversionTracked, setConversionTracked] = useState(false);
  const [analyzingWebsite, setAnalyzingWebsite] = useState(false);
  const [websiteAnalysisError, setWebsiteAnalysisError] = useState('');
  const [websiteAnalysisSuccess, setWebsiteAnalysisSuccess] = useState(false);
  const [wordpressAccordionOpen, setWordpressAccordionOpen] = useState(false);
  const [htmlAccordionOpen, setHtmlAccordionOpen] = useState(false);
  const [webflowAccordionOpen, setWebflowAccordionOpen] = useState(false);
  const [shopifyAccordionOpen, setShopifyAccordionOpen] = useState(false);
  const [wixAccordionOpen, setWixAccordionOpen] = useState(false);
  const [tildaAccordionOpen, setTildaAccordionOpen] = useState(false);
  const [squarespaceAccordionOpen, setSquarespaceAccordionOpen] = useState(false);
  const [gtmAccordionOpen, setGtmAccordionOpen] = useState(false);
  // deep link: /dashboard?tab=widget&open=whatsapp → opens the WhatsApp QR block right away
  const [integrationCategory, setIntegrationCategory] = useState(() => {
    const o = new URLSearchParams(window.location.search).get('open');
    return ['website', 'whatsapp', 'instagram', 'ident', 'vk', 'voice', 'altegio'].includes(o) ? o : null;
  });
  useEffect(() => {
    if (!integrationCategory || new URLSearchParams(window.location.search).get('open') !== integrationCategory) return;
    const tm = setTimeout(() => document.getElementById('integrations-block')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 600);
    return () => clearTimeout(tm);
  }, []);
  const [twilioAccordionOpen, setTwilioAccordionOpen] = useState(false);
  const [twilioStatus, setTwilioStatus] = useState({ enabled: false, has_credentials: false, phone_number: null, webhook_url: null });
  const [twilioLoading, setTwilioLoading] = useState(false);
  const [twilioCredentials, setTwilioCredentials] = useState({
    account_sid: '',
    auth_token: '',
    phone_number: '',
    voice_language: 'ru-RU',
    voice_rate: '100'
  });
  const [showDeveloperEmailModal, setShowDeveloperEmailModal] = useState(false);
  const [developerEmail, setDeveloperEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Support tickets states
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [newTicketSubject, setNewTicketSubject] = useState('');
  const [newTicketMessage, setNewTicketMessage] = useState('');
  const [newTicketPriority, setNewTicketPriority] = useState('medium');
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [ticketReply, setTicketReply] = useState('');

  // Google Ads Conversion Tracking - fires once when user enters dashboard
  useEffect(() => {
    if (!conversionTracked && typeof window.gtag === 'function') {
      // Track conversion event for Google Ads
      window.gtag('event', 'conversion', {
        'send_to': 'AW-16535299481/FslQCMan-LUZEJnL0sw9'
      });
      setConversionTracked(true);
      console.log('✅ Google Ads conversion tracked: User entered dashboard');
    }
  }, [conversionTracked]);
  const [widgetStatus, setWidgetStatus] = useState('unchecked'); // unchecked, checking, active, inactive
  const [lastActivityTime, setLastActivityTime] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [dateFilter, setDateFilter] = useState('30'); // '1', '7', '30', 'custom'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const DASHBOARD_TABS = ['settings', 'bookings', 'clients', 'team', 'subscription', 'widget', 'marketing', 'minisite', 'support', 'analytics'];
  const [activeTab, setActiveTab] = useState(() => {
    const p = new URLSearchParams(window.location.search).get('tab');
    const mapped = p === 'billing' ? 'subscription' : p === 'integrations' ? 'widget' : p;
    return DASHBOARD_TABS.includes(mapped) ? mapped : 'settings';
  });
  const resolveReschedule = async (bookingId, action) => {
    try {
      const dateEl = document.getElementById(`rs-date-${bookingId}`);
      const timeEl = document.getElementById(`rs-time-${bookingId}`);
      const r = await axios.post(`${API}/bookings/${bookingId}/reschedule`, {
        action,
        new_date: dateEl?.value || null,
        new_time: timeEl?.value || null,
      });
      const via = r.data?.sent_via;
      toast.success(
        action === 'confirm'
          ? `Перенос подтверждён${via ? ` — клиенту отправлено в ${via === 'instagram' ? 'Instagram' : 'WhatsApp'}` : ''}`
          : 'Перенос отклонён, клиент уведомлён'
      );
      fetchData(true);
    } catch (e) {
      toast.error('Не удалось обработать перенос');
    }
  };

  const handleTabChange = (val) => {
    setActiveTab(val);
    if (embedded) return;
    const url = new URL(window.location);
    url.searchParams.set('tab', val);
    window.history.replaceState({}, '', url);
  };

  // Embedded mode: tab controlled by parent (CalendarPage settings menu)
  useEffect(() => {
    if (!embedded || !tabProp) return;
    const mapped = tabProp === 'billing' ? 'subscription' : tabProp;
    if (DASHBOARD_TABS.includes(mapped)) setActiveTab(mapped);
    // eslint-disable-next-line
  }, [embedded, tabProp]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [billingPeriod, setBillingPeriod] = useState('monthly'); // 'monthly' or 'yearly'
  const [adminPerms, setAdminPerms] = useState(null); // staff-admin section permissions

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("user") || "{}");
    if (!embedded && userData.role === 'master') { navigate('/calendar'); return; }
    if (userData.role === 'admin') {
      axios.get(`${API}/team/me`).then((r) => {
        const p = { bookings: true, clients: true, team: true, ...(r.data.permissions || {}) };
        setAdminPerms(p);
        if (embedded) return;
        const allowed = ['team'].filter((k) => p[k]);
        if (!allowed.length) { navigate('/calendar'); return; }
        const cur = new URL(window.location).searchParams.get('tab');
        if (!allowed.includes(cur)) handleTabChange(allowed[0]);
      }).catch(() => { if (!embedded) navigate('/staff'); });
    }
    setUser(userData);
    
    // Clean up old non-user-specific promocode key
    localStorage.removeItem("appliedPromocode");
    
    // Restore promocode from localStorage (unique per user)
    if (userData.id) {
      const savedPromocode = localStorage.getItem(`promocode_${userData.id}`);
      if (savedPromocode) {
        try {
          const promoData = JSON.parse(savedPromocode);
          setPromocodeApplied(promoData);
          setPromocodeInput(promoData.code);
          console.log('✅ Restored promocode for user:', userData.email, promoData.code);
        } catch (e) {
          console.error('Error restoring promocode:', e);
          localStorage.removeItem(`promocode_${userData.id}`);
        }
      }
    }
    
    fetchData();
    if (userData.role !== 'admin') {
      fetchSubscriptionInfo();
      fetchBoosterBalance();
      fetchPaymentHistory();
      fetchTickets();
      fetchTwilioStatus();
    }
    detectUserCountry();

    // Check for payment return
    checkPaymentReturn();

    // Auto-refresh bookings every 10 seconds
    const intervalId = setInterval(() => {
      fetchData(true); // silent refresh
    }, 10000);

    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, []);

  // Detect user country for payment system selection
  const detectUserCountry = async () => {
    try {
      console.log('🔍 Detecting user country...');
      // Try to detect country via IP geolocation
      const response = await axios.get('https://ipapi.co/json/', { timeout: 5000 });
      const country = response.data.country_code;
      const city = response.data.city;
      const ip = response.data.ip;
      
      console.log(`📍 Location detected: ${city}, ${country} (IP: ${ip})`);
      setUserCountry(country);
      
      // Payment system: Stripe (EUR) for everyone — Tinkoff routing disabled
      setPaymentSystem('stripe');
      if (false && country === 'RU') {
        setPaymentSystem('tinkoff');
        console.log('🇷🇺 Russian user detected - using Tinkoff payments (RUB)');
        // toast.success('Определена Россия - используется Tinkoff (₽)', { duration: 3000 });
      } else {
        setPaymentSystem('stripe');
        console.log(`🌍 International user detected (${country}) - using Stripe payments (EUR)`);
        // toast.info(`Detected ${country} - using Stripe (€)`, { duration: 3000 });
      }
    } catch (error) {
      // Default to Stripe if detection fails
      console.error('⚠️ Country detection failed:', error.message);
      console.log('Defaulting to Stripe.');
      setPaymentSystem('stripe');
      setUserCountry(null);
      // toast.warning('Не удалось определить страну. Выберите способ оплаты вручную.', { duration: 4000 });
    }
  };

  const fetchData = async (silent = false) => {
    try {
      const [salonsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/salons`),
        axios.get(`${API}/bookings`)
      ]);

      if (salonsRes.data.length > 0) {
        const salonData = salonsRes.data[0];
        setSalon(salonData);
        // Only update settings on initial load, not on auto-refresh
        if (!silent) {
          const widgetSettings = salonData.widget_settings || {};
          
          // Use translated defaults if values are empty or default Russian values
          // Don't force default values - allow empty strings
          const botName = widgetSettings.bot_name !== undefined ? widgetSettings.bot_name : t('settings.defaultBotName');
          const welcomeMessage = widgetSettings.welcome_message !== undefined ? widgetSettings.welcome_message : t('settings.defaultWelcomeMessage');
          
          setSettings({
            telegram_chat_id: salonData.telegram_chat_id || "",
            notification_emails: salonData.notification_emails || "",
            email_notifications: salonData.email_notifications === true, // explicit boolean check
            salon_info: salonData.salon_info || "",
            job_reply: salonData.job_reply || "",
            business_type: salonData.business_type || "general",
            product_feed_url: salonData.product_feed_url || "",
            currency_symbol: salonData.currency_symbol || "€",
            avg_ticket: salonData.avg_ticket ?? "",
            calendar_enabled: salonData.calendar_enabled !== false,
            operating_mode: salonData.operating_mode || (salonData.calendar_enabled === false ? "crm_only" : "autopilot"),
            manual_pause_hours: salonData.manual_pause_hours ?? 6,
            ai_reply_delay_sec: salonData.ai_reply_delay_sec ?? 0,
            ai_schedule: salonData.ai_schedule || null,
            voice_reply_mode: salonData.voice_reply_mode || "off",
            voice_id: salonData.voice_id || null,
            hide_chatity_branding: salonData.hide_chatity_branding === true,
            ai_intro_text: salonData.ai_intro_text || "",
            widget_settings: {
              primary_color: widgetSettings.primary_color || "#000000",
              secondary_color: widgetSettings.secondary_color || "#FFFFFF",
              button_color: widgetSettings.button_color || "#000000",
              bot_name: botName,
              welcome_message: welcomeMessage,
              balloon_enabled: widgetSettings.balloon_enabled !== undefined ? widgetSettings.balloon_enabled : true,
              balloon_delay: widgetSettings.balloon_delay || 5,
              balloon_message: widgetSettings.balloon_message || t('settings.defaultBalloonMessage') || "Здравствуйте! Помогу записаться на услугу 👋",
              auto_open_enabled: widgetSettings.auto_open_enabled || false,
              auto_open_delay: widgetSettings.auto_open_delay || 15
            }
          });
        }
      }

      // Check for new bookings
      const newBookings = bookingsRes.data;
      if (!silent && bookings.length > 0 && newBookings.length > bookings.length) {
        toast.success(`${t('bookings.newBooking')} ${newBookings.length}`);
      }
      setBookings(newBookings);
    } catch (error) {
      if (!silent) {
        toast.error(t('common.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionInfo = async () => {
    try {
      const response = await axios.get(`${API}/subscription/info`);
      setSubscriptionInfo(response.data);
    } catch (error) {
      console.error("Error fetching subscription info:", error);
    }
  };

  const fetchBoosterBalance = async () => {
    try {
      const response = await axios.get(`${API}/billing/booster/balance`);
      setBoosterBalance(response.data);
    } catch (error) {
      // Silent — endpoint requires auth, may fail before login
    }
    try {
      const a = await axios.get(`${API}/billing/addons`);
      setAddonsInfo(a.data);
    } catch (error) { /* silent */ }
  };

  const handleBuyAddon = async (addonType) => {
    setAddonLoading(addonType);
    try {
      const res = await addonCheckout(addonType, {
        t,
        onAttached: () => { toast.success(t('addons.attached')); fetchBoosterBalance(); },
      });
      if (!res?.cancelled && !res?.url && res?.status !== 'attached') toast.error('Не удалось создать платёжную сессию');
    } catch (error) {
      toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || 'Ошибка оплаты');
    } finally {
      setAddonLoading('');
    }
  };

  const handleBuyBooster = async () => {
    setBoosterLoading(true);
    try {
      const response = await axios.post(`${API}/billing/booster/checkout`, {
        pack_id: 'booster_100',
        qty: boosterQty,
        origin_url: window.location.origin,
      });
      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Не удалось создать платёжную сессию');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка оплаты');
    } finally {
      setBoosterLoading(false);
    }
  };


  const fetchPaymentHistory = async () => {
    try {
      const response = await axios.get(`${API}/payments/history`);
      setPaymentHistory(response.data.transactions || []);
    } catch (error) {
      console.error("Error fetching payment history:", error);
    }
  };


  const fetchAnalytics = async () => {
    try {
      const params = new URLSearchParams();
      
      if (dateFilter === 'custom' && customStartDate && customEndDate) {
        params.append('start_date', customStartDate);
        params.append('end_date', customEndDate);
      } else {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - parseInt(dateFilter));
        params.append('start_date', startDate.toISOString().split('T')[0]);
        params.append('end_date', endDate.toISOString().split('T')[0]);
      }
      
      const response = await axios.get(`${API}/bookings/analytics?${params.toString()}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    }
  };

  // Support tickets functions
  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API}/tickets`);
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Ошибка загрузки тикетов');
    }
  };

  const fetchTicketDetails = async (ticketId) => {
    try {
      const response = await axios.get(`${API}/tickets/${ticketId}`);
      setSelectedTicket(response.data.ticket);
      setTicketMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      toast.error('Ошибка загрузки тикета');
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    
    if (!newTicketSubject.trim() || !newTicketMessage.trim()) {
      toast.error('Заполните все поля');
      return;
    }

    try {
      const response = await axios.post(`${API}/tickets`, {
        subject: newTicketSubject,
        message: newTicketMessage,
        priority: newTicketPriority
      });

      toast.success('Тикет создан успешно!');
      setShowCreateTicket(false);
      setNewTicketSubject('');
      setNewTicketMessage('');
      setNewTicketPriority('medium');
      fetchTickets();
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error('Ошибка создания тикета');
    }
  };

  const handleSendTicketReply = async (e) => {
    e.preventDefault();
    
    if (!ticketReply.trim()) {
      return;
    }

    try {
      await axios.post(`${API}/tickets/${selectedTicket.id}/messages`, {
        message: ticketReply
      });

      setTicketReply('');
      fetchTicketDetails(selectedTicket.id);
      toast.success('Сообщение отправлено');
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Ошибка отправки сообщения');
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [dateFilter, customStartDate, customEndDate]);

  const checkPaymentReturn = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const paymentStatus = urlParams.get('payment');

    if (sessionId && paymentStatus === 'success') {
      toast.info(t('subscription.checkingPayment') || 'Проверка статуса платежа...');
      pollPaymentStatus(sessionId);
      // Clean URL
      const cleanUrl = new URL(window.location);
      cleanUrl.searchParams.delete('session_id');
      cleanUrl.searchParams.delete('payment');
      window.history.replaceState({}, document.title, cleanUrl);
    } else if (paymentStatus === 'cancelled') {
      toast.error(t('subscription.paymentCancelled') || 'Оплата отменена');
      const cleanUrl2 = new URL(window.location);
      cleanUrl2.searchParams.delete('session_id');
      cleanUrl2.searchParams.delete('payment');
      window.history.replaceState({}, document.title, cleanUrl2);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 15; // Increased from 5 to 15
    // Exponential backoff: 2s, 3s, 4s, 6s, 8s, 10s...
    const getInterval = (attempt) => {
      if (attempt < 3) return 2000; // First 3 attempts: 2 seconds
      if (attempt < 6) return 3000; // Next 3: 3 seconds
      if (attempt < 10) return 5000; // Next 4: 5 seconds
      return 10000; // Rest: 10 seconds
    };

    if (attempts >= maxAttempts) {
      toast.error(
        t('subscription.paymentTimeout') || 
        'Проверка платежа заняла слишком много времени. Если деньги списались, подписка активируется автоматически в течение нескольких минут.',
        { duration: 8000 }
      );
      // Still refresh data in case webhook processed it
      fetchSubscriptionInfo();
      fetchPaymentHistory();
      return;
    }

    try {
      console.log(`🔄 Checking payment status (attempt ${attempts + 1}/${maxAttempts})...`);
      
      const response = await axios.get(`${API}/stripe/checkout-status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        if (typeof window.fbq === 'function') {
          const purchaseData = { currency: (response.data.currency || 'eur').toUpperCase() };
          if (response.data.amount_total) purchaseData.value = response.data.amount_total / 100;
          window.fbq('track', 'Purchase', purchaseData);
        }
        const isBooster = response.data.type === 'booster';
        if (isBooster) {
          toast.success(`✅ Куплено +${response.data.added || 100} диалогов!`, { duration: 5000 });
        } else {
          toast.success(t('subscription.paymentSuccess') || 'Оплата успешна! Ваш план обновлен', {
            duration: 5000
          });
        }
        // Refresh subscription info, booster balance and payment history
        fetchSubscriptionInfo();
        fetchBoosterBalance();
        fetchPaymentHistory();
        fetchData();
        return;
      } else if (response.data.status === 'expired') {
        toast.error(t('subscription.sessionExpired') || 'Сессия оплаты истекла');
        return;
      } else if (response.data.payment_status === 'unpaid') {
        // Payment not completed yet
        console.log(`⏳ Payment still pending, will retry in ${getInterval(attempts)}ms`);
      }

      // Continue polling with exponential backoff
      const interval = getInterval(attempts);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), interval);
      
    } catch (error) {
      console.error('Error checking payment status:', error);
      
      // Don't show error on every attempt, only on last one
      if (attempts >= maxAttempts - 1) {
        toast.error(
          t('subscription.paymentCheckError') || 
          'Ошибка проверки статуса платежа. Если деньги списались, подписка активируется автоматически.'
        );
      } else {
        // Retry even on error
        const interval = getInterval(attempts);
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), interval);
      }
    }
  };

  // Validate promocode
  const validatePromocode = async () => {
    if (!promocodeInput || promocodeInput.trim().length < 3) {
      setPromocodeError(t('promocode.enterCode') || 'Введите промокод');
      return;
    }

    try {
      const planWithPeriod = billingPeriod === 'yearly' ? 'starter_yearly' : 'starter';
      const response = await axios.post(`${API}/promocodes/validate`, {
        code: promocodeInput.toUpperCase(),
        plan: planWithPeriod
      });
      
      setPromocodeApplied(response.data);
      setPromocodeError('');
      
      // Yearly-only codes: auto-switch billing period
      const ap = response.data.applicable_plans || ['all'];
      if (!ap.includes('all') && ap.every(p => p.endsWith('_yearly'))) {
        setBillingPeriod('yearly');
        toast.info(t('promocode.yearlyOnly') || 'Промокод действует только на годовые тарифы');
      }
      
      // Save to localStorage (unique per user)
      if (user?.id) {
        localStorage.setItem(`promocode_${user.id}`, JSON.stringify(response.data));
        console.log('💾 Promocode saved for user:', user.email, response.data.code);
      }
      
      toast.success(t('promocode.applied') || response.data.message);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || t('promocode.error') || 'Неверный промокод';
      setPromocodeError(errorMsg);
      setPromocodeApplied(null);
      if (user?.id) {
        localStorage.removeItem(`promocode_${user.id}`);
      }
      toast.error(errorMsg);
    }
  };

  const clearPromocode = () => {
    setPromocodeInput('');
    setPromocodeApplied(null);
    setPromocodeError('');
    if (user?.id) {
      localStorage.removeItem(`promocode_${user.id}`);
      console.log('🗑️ Promocode removed for user:', user.email);
    }
  };

  const redeemPromocode = async () => {
    if (!promocodeApplied?.code) return;
    try {
      const res = await axios.post(`${API}/promocodes/redeem`, { code: promocodeApplied.code });
      toast.success(res.data.message || 'Активировано!', { duration: 8000 });
      clearPromocode();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка активации промокода');
    }
  };

  // Get price with correct currency based on payment system
  const getPrice = (plan) => {
    const currency = paymentSystem === 'tinkoff' ? 'rub' : 'eur';
    let price = PRICING[currency][billingPeriod][plan];
    const currencySymbol = paymentSystem === 'tinkoff' ? '₽' : '€';
    
    // Apply promocode discount if applied (only to applicable plans)
    let discount = 0;
    if (promocodeApplied && promocodeApplied.promo_type !== 'free_months') {
      const planWithPeriod = billingPeriod === 'yearly' ? `${plan}_yearly` : plan;
      const ap = promocodeApplied.applicable_plans || ['all'];
      if (ap.includes('all') || ap.includes(planWithPeriod)) {
        discount = promocodeApplied.discount_percent || 0;
        price = price * (1 - discount / 100);
      }
    }
    
    return { price, currency, currencySymbol, discount };
  };

  const handleUpgradePlan = async (plan) => {
    try {
      const originUrl = window.location.origin;
      // Add _yearly suffix if yearly billing is selected
      const planWithPeriod = billingPeriod === 'yearly' ? `${plan}_yearly` : plan;
      
      // Choose payment endpoint based on detected country
      const endpoint = paymentSystem === 'tinkoff' 
        ? `${API}/tinkoff/create-checkout`
        : `${API}/stripe/create-checkout`;
      
      console.log(`🛒 Creating checkout with ${paymentSystem} for plan ${planWithPeriod}`);
      
      const response = await axios.post(endpoint, { 
        plan: planWithPeriod,
        origin_url: originUrl,
        promocode: promocodeApplied ? promocodeApplied.code : null
      });
      
      // Check if upgrade was done without payment (using credit)
      if (response.data.redirect_to_dashboard) {
        const successMsg = response.data.message || 
          t('subscription.upgradeSuccess') || 
          'План успешно обновлен';
        toast.success(successMsg, { duration: 5000 });
        
        // Refresh data
        fetchData();
        fetchSubscriptionInfo();
        return;
      }
      
      // Show prorated amount info if applicable
      if (response.data.prorated && response.data.credit > 0) {
        const creditMsg = t('subscription.creditApplied') || 
          `Применен кредит €${response.data.credit.toFixed(2)}. К оплате: €${response.data.amount.toFixed(2)} вместо €${response.data.full_price.toFixed(2)}`;
        toast.info(creditMsg, { duration: 5000 });
      }
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(t('subscription.checkoutError') || "Ошибка создания сессии оплаты");
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm(t('subscription.cancelConfirm'))) return;
    try {
      await axios.post(`${API}/subscription/cancel`, {});
      toast.success(t('subscription.cancelDone'));
      fetchSubscriptionInfo();
      fetchData(true);
    } catch (error) {
      toast.error(t('subscription.cancelError'));
    }
  };

  const handleManageBilling = async () => {
    try {
      const response = await axios.post(`${API}/stripe/customer-portal`, {
        origin_url: window.location.origin
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Customer portal error:', error);
      toast.error(t('subscription.portalError') || 'Не удалось открыть портал управления подпиской');
    }
  };

  const handleDownloadInvoice = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/payments/${sessionId}/invoice`);
      if (response.data.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (error) {
      toast.error(t('subscription.invoiceUnavailable') || 'Инвойс недоступен для этого платежа');
    }
  };

  const getButtonText = (plan) => {
    // Default text if subscription info not loaded yet
    if (!subscriptionInfo) {
      return t('subscription.selectPlan');
    }
    
    if (!subscriptionInfo.is_paid) {
      return t('subscription.selectPlan');
    }
    
    const currentPlan = subscriptionInfo.plan;
    
    // Same plan
    if (currentPlan === plan) {
      if (billingPeriod === 'yearly') {
        // User is on monthly, viewing yearly - offer upgrade
        return t('subscription.upgradeToYearly') || 'Перейти на годовой';
      } else {
        // Already on this plan
        return t('subscription.currentPlanBadge');
      }
    } else {
      // Different plan
      return t('subscription.selectPlan');
    }
  };

  const isButtonDisabled = (plan) => {
    // Not disabled if subscription info not loaded yet
    if (!subscriptionInfo) {
      return false;
    }
    
    if (!subscriptionInfo.is_paid) {
      return false;
    }
    
    const currentPlan = subscriptionInfo.plan;
    
    // Disable if same plan and same period (monthly)
    return currentPlan === plan && billingPeriod === 'monthly';
  };

  const handleAnalyzeWebsite = async () => {
    setAnalyzingWebsite(true);
    setWebsiteAnalysisError('');
    setWebsiteAnalysisSuccess(false);
    
    try {
      const response = await axios.post(
        `${API}/analyze-website`,
        { url: salon.website_url },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      
      if (response.data.success) {
        // Update salon_info with extracted data
        setSettings({
          ...settings,
          salon_info: response.data.formatted_text
        });
        setWebsiteAnalysisSuccess(true);
        toast.success(t('settings.websiteAnalyzedSuccess') || 'Website analyzed successfully!');
        
        // Hide success message after 5 seconds
        setTimeout(() => setWebsiteAnalysisSuccess(false), 5000);
      } else {
        setWebsiteAnalysisError(response.data.error || 'Failed to analyze website');
        toast.error(response.data.error || 'Failed to analyze website');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message || 'Error analyzing website';
      setWebsiteAnalysisError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setAnalyzingWebsite(false);
    }
  };

  const handleSendToDeveloper = async () => {
    if (!developerEmail || !developerEmail.includes('@')) {
      toast.error(t('common.invalidEmail') || 'Invalid email');
      return;
    }
    
    setSendingEmail(true);
    try {
      await axios.post(`${API}/send-webmaster-email`, {
        webmaster_email: developerEmail,
        widget_key: salon?.id,
        salon_name: salon?.salon_name || 'Client',
        language: lang || 'en'
      });
      
      toast.success(t('integrations.emailSent'));
      setShowDeveloperEmailModal(false);
      setDeveloperEmail('');
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(t('common.error') || 'Error sending email');
    } finally {
      setSendingEmail(false);
    }
  };

  // Twilio Voice AI functions
  const fetchTwilioStatus = async () => {
    try {
      const response = await axios.get(`${API}/voice/twilio/status`);
      setTwilioStatus(response.data);
      // Load saved credentials
      if (response.data.has_credentials) {
        setTwilioCredentials({
          account_sid: response.data.account_sid || '',
          auth_token: response.data.auth_token || '',
          phone_number: response.data.phone_number || '',
          voice_language: response.data.voice_language || 'ru-RU',
          voice_rate: response.data.voice_rate || '100'
        });
      }
    } catch (error) {
      console.error('Error fetching Twilio status:', error);
    }
  };

  const handleEnableTwilio = async () => {
    // Validate credentials
    if (!twilioCredentials.account_sid || !twilioCredentials.auth_token || !twilioCredentials.phone_number) {
      toast.error(t('integrations.twilio.credentialsRequired') || 'Please fill in all Twilio credentials');
      return;
    }

    setTwilioLoading(true);
    try {
      const response = await axios.post(`${API}/voice/twilio/enable`, twilioCredentials);
      
      setTwilioStatus({
        enabled: true,
        has_credentials: true,
        phone_number: response.data.phone_number,
        webhook_url: response.data.webhook_url
      });
      
      toast.success(t('integrations.twilio.enabled'));
    } catch (error) {
      console.error('Error enabling Twilio:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setTwilioLoading(false);
    }
  };

  const handleDisableTwilio = async () => {
    setTwilioLoading(true);
    try {
      await axios.post(`${API}/voice/twilio/disable`);
      
      setTwilioStatus({
        enabled: false,
        has_credentials: twilioStatus.has_credentials,
        phone_number: null,
        webhook_url: null
      });
      
      toast.success(t('integrations.twilio.disabled'));
    } catch (error) {
      console.error('Error disabling Twilio:', error);
      toast.error(t('common.error'));
    } finally {
      setTwilioLoading(false);
    }
  };


  const [saving, setSaving] = useState(false);
  const handleSaveSettings = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        ...settings,
        avg_ticket: settings.avg_ticket === "" || settings.avg_ticket === null ? null : parseFloat(settings.avg_ticket),
      };
      const response = await axios.put(`${API}/salons/${salon.id}`, payload);
      toast.success(t('settings.saveSuccess'));
      setSalon(response.data);
    } catch (error) {
      console.error('Save error:', error);
      toast.error(t('settings.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotifications = async () => {
    try {
      toast.loading('Отправка тестовых уведомлений...');
      const response = await axios.post(`${API}/test-notifications`);
      
      const results = response.data.results;
      let message = '🧪 Результаты теста:\n\n';
      
      // Telegram results
      if (results.telegram.configured) {
        if (results.telegram.sent) {
          message += '✅ Telegram: Сообщение отправлено\n';
        } else {
          message += `❌ Telegram: ${results.telegram.error}\n`;
        }
      } else {
        message += '⚠️ Telegram: Не настроен\n';
      }
      
      // Email results
      if (results.email.configured) {
        if (results.email.sent) {
          message += '✅ Email: Сообщение отправлено';
        } else {
          message += `❌ Email: ${results.email.error}`;
        }
      } else {
        message += '⚠️ Email: Не настроен';
      }
      
      toast.dismiss();
      toast.success(message, { duration: 8000 });
    } catch (error) {
      toast.dismiss();
      console.error('Test notifications error:', error);
      toast.error('Ошибка при тестировании уведомлений: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setIsAuthenticated(false);
    toast.success(t('dashboard.logoutSuccess'));
    // Redirect to landing page (not auth form)
    setTimeout(() => {
      window.location.href = '/';
    }, 300);
  };

  const getWidgetCode = () => {
    const baseUrl = window.location.origin;
    // New universal embed code with query parameter - never needs to be changed by clients
    return `<script src="${baseUrl}/api/widget/embed.js?id=${salon?.id}"></script>`;
  };

  const copyWidgetCode = () => {
    navigator.clipboard.writeText(getWidgetCode());
    toast.success(t('widget.copied'));
  };

  const checkWidgetInstallation = async () => {
    if (!salon?.id) return;
    
    setWidgetStatus('checking');
    
    try {
      // Check if widget configuration is accessible
      const configResponse = await axios.get(`${API}/widget/${salon.id}/config`);
      
      if (configResponse.status === 200) {
        // Check if there were any recent messages (last 7 days)
        const messagesResponse = await axios.get(`${API}/chat/history/salon/${salon.id}`);
        const messages = messagesResponse.data || [];
        
        if (messages.length > 0) {
          const lastMessage = messages[0]; // First message is most recent (sorted desc)
          setLastActivityTime(new Date(lastMessage.created_at).toLocaleString());
          setWidgetStatus('active');
          toast.success(t('widget.status.successToast'));
        } else {
          setWidgetStatus('configured');
          toast.info(t('widget.status.configuredToast'));
        }
      }
    } catch (error) {
      setWidgetStatus('inactive');
      toast.error(t('widget.status.errorToast'));
    }
  };

  if (loading) {
    return (
      <div className={embedded ? "py-16 text-center text-gray-400" : "min-h-screen bg-black flex items-center justify-center"}>
        <div className={embedded ? "" : "text-white text-xl"}>{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className={embedded ? "bg-black text-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" : "min-h-screen bg-black text-white"}>
      {!embedded && (
      <header className="border-b-2 border-white bg-black sticky top-0 z-50">
        <div className="container mx-auto px-3 md:px-4 py-3 md:py-4">
          {/* Mobile layout */}
          <div className="flex md:hidden justify-between items-center mb-2">
            <h1 className="text-xl font-bold" data-testid="dashboard-title"><img src="/logo-header.png" alt="Chatity" className="h-6 w-auto invert" /></h1>
            <div className="flex items-center gap-2">
              <Button
                data-testid="calendar-link-button"
                onClick={() => navigate('/calendar')}
                variant="outline"
                size="sm"
                className={`border-2 border-white text-white hover:bg-white hover:text-black p-2 font-bold ${settings.calendar_enabled ? '' : 'hidden'}`}
              >
                📅
              </Button>
              <Button
                data-testid="logout-button"
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="border-2 border-white text-white hover:bg-white hover:text-black p-2"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="md:hidden">
            <span className="text-xs text-gray-400" data-testid="user-email-header">{user?.email}</span>
          </div>
          
          {/* Desktop layout */}
          <div className="hidden md:flex justify-between items-center">
            <h1 className="text-3xl font-bold" data-testid="dashboard-title"><img src="/logo-header.png" alt="Chatity" className="h-9 w-auto invert" /></h1>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600 font-medium" data-testid="user-email-header">{user?.email}</span>
              <Button
                data-testid="header-support-btn"
                onClick={() => { setActiveTab('support'); fetchTickets(); }}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-black"
                title={t('tabs.support') || 'Support'}
              >
                <LifeBuoy className="w-4 h-4 mr-2" />
                {t('tabs.support') || 'Support'}
              </Button>
              <Button
                data-testid="calendar-link-button-desktop"
                onClick={() => navigate('/calendar')}
                variant="outline"
                className={`border-2 border-white text-white hover:bg-white hover:text-black font-bold ${settings.calendar_enabled ? '' : 'hidden'}`}
              >
                📅 {t('tabs.calendar')}
              </Button>
              <Button
                data-testid="logout-button"
                onClick={handleLogout}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-black"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('dashboard.logout')}
              </Button>
            </div>
          </div>
        </div>
      </header>
      )}

      {!embedded && <AnnouncementBar />}

      <main className="container mx-auto px-3 md:px-4 py-4 md:py-8">
        {/* Onboarding Guide for new users */}
        <OnboardingGuide onNavigate={handleTabChange} />

        <ReferralBanner lang={lang} />

        {/* Subscription Banner - Mobile optimized */}
        {subscriptionInfo && !subscriptionInfo.is_paid && (
          <Card className={`border-2 md:border-4 mb-4 md:mb-6 ${
            subscriptionInfo.trial_days_left > 0 ? 'bg-blue-50 border-blue-500' : 'bg-green-50 border-green-500'
          }`}>
            <CardContent className="pt-4 md:pt-6 pb-4 md:pb-6">
              <div className="flex flex-col gap-3 md:gap-4">
                <div className="flex-1">
                  <h3 className="text-lg md:text-2xl font-bold text-black mb-2">
                    {subscriptionInfo.trial_days_left > 0 
                      ? `🎁 ${t('subscription.trialBannerTitle')}: ${subscriptionInfo.trial_days_left} ${subscriptionInfo.trial_days_left === 1 ? t('subscription.day') : t('subscription.days')} ${t('subscription.remaining')}`
                      : `🆓 ${t('subscription.freeForever') !== 'subscription.freeForever' ? t('subscription.freeForever') : 'Вы на тарифе Free — календарь и мини-сайт бесплатны навсегда'}`}
                  </h3>
                  <p className="text-gray-700 text-xs md:text-sm mb-3">
                    {subscriptionInfo.trial_days_left > 0 
                      ? t('subscription.selectPlanBeforeExpiry')
                      : (t('subscription.freeForeverHint') !== 'subscription.freeForeverHint' ? t('subscription.freeForeverHint') : 'Подключите ИИ-администратора от €39/мес — он сам отвечает клиентам и записывает их')}
                  </p>
                  
                  <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                    <div className="text-xs md:text-sm font-semibold text-gray-700">
                      {t('subscription.currentPlan')}: <span className="text-black uppercase">{subscriptionInfo.plan}</span>
                    </div>
                    <div className="text-xs md:text-sm text-gray-600">
                      {t('subscription.conversations')}: {subscriptionInfo.conversations_used} / {subscriptionInfo.is_unlimited ? '∞' : subscriptionInfo.conversations_limit}
                    </div>
                  </div>
                  
                  {!subscriptionInfo.is_unlimited && (
                    <div className="mt-3">
                      <div className="w-full bg-gray-200 rounded-full h-2 md:h-3 border border-black md:border-2">
                        <div 
                          className={`h-full rounded-full transition-all ${
                            subscriptionInfo.usage_percentage >= 80 ? 'bg-red-500' : 
                            subscriptionInfo.usage_percentage >= 50 ? 'bg-yellow-500' : 
                            'bg-green-500'
                          }`}
                          style={{ width: `${subscriptionInfo.usage_percentage}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-600 mt-1">{subscriptionInfo.usage_percentage}% {t('subscription.used')}</p>
                    </div>
                  )}
                </div>
                
                <Button
                  onClick={() => setActiveTab('subscription')}
                  className="bg-black text-white hover:bg-gray-800 px-6 md:px-8 py-4 md:py-6 text-base md:text-lg font-bold border-2 border-black w-full md:w-auto"
                >
                  {subscriptionInfo.trial_days_left > 0 ? t('subscription.selectPlanButton') : t('subscription.payButton')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Usage Stats for Paid Users */}
        {subscriptionInfo && subscriptionInfo.is_paid && (
          <Card className="bg-white border-4 border-black mb-6">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-black mb-2">
                    ✅ {t('subscription.activeSubscription')}: <span className="uppercase">{subscriptionInfo.plan}</span>
                  </h3>
                  <div className="flex flex-col gap-2 text-sm">
                    <div className="flex items-center gap-6 flex-wrap">
                      <div>
                        <span className="text-gray-600">{t('subscription.conversations')}:</span>{' '}
                        <span className="font-bold text-black">
                          {subscriptionInfo.conversations_used} / {subscriptionInfo.is_unlimited ? '∞' : subscriptionInfo.conversations_limit}
                        </span>
                      </div>
                      {boosterBalance.bonus_conversations_left > 0 && (
                        <div data-testid="booster-balance-badge" className="bg-yellow-100 border-2 border-black px-2 py-1 rounded text-xs font-bold">
                          💎 +{boosterBalance.bonus_conversations_left} бонусных
                        </div>
                      )}
                      {!subscriptionInfo.is_unlimited && subscriptionInfo.usage_percentage >= 80 && (
                        <div className="text-orange-600 font-semibold">
                          ⚠️ {100 - subscriptionInfo.usage_percentage}% {t('subscription.remaining')}
                        </div>
                      )}
                    </div>
                    {subscriptionInfo.renewal_date && (
                      <div className="text-sm text-gray-600">
                        🔄 <span className="font-medium">{t('subscription.renewalDate') || 'Следующее продление'}:</span>{' '}
                        <span className="font-semibold text-black">
                          {new Date(subscriptionInfo.renewal_date).toLocaleDateString(lang === 'ru' ? 'ru-RU' : lang === 'fr' ? 'fr-FR' : 'en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {!subscriptionInfo.is_unlimited && (
                    <div className="mt-3 max-w-md">
                      <div className="w-full bg-gray-200 rounded-full h-2 border border-gray-300">
                        <div 
                          className={`h-full rounded-full transition-all ${
                            subscriptionInfo.usage_percentage >= 80 ? 'bg-orange-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${subscriptionInfo.usage_percentage}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                {subscriptionInfo.plan !== 'unlimited' && (
                  <Button
                    onClick={() => setActiveTab('subscription')}
                    variant="outline"
                    className="border-2 border-black text-black hover:bg-black hover:text-white whitespace-nowrap"
                  >
                    {t('subscription.upgradePlan')}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Booster pack — show CTA when paid plan is near/at limit OR when bonus is running low */}
        {subscriptionInfo && subscriptionInfo.is_paid && !subscriptionInfo.is_unlimited && (
          (subscriptionInfo.usage_percentage >= 70 || boosterBalance.bonus_conversations_left > 0) && (
          <Card className="bg-yellow-50 border-4 border-black mb-6" data-testid="booster-pack-card">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-lg md:text-xl font-bold text-black mb-1">
                    💎 Дополнительные диалоги
                  </h3>
                  <p className="text-sm text-gray-700">
                    {boosterBalance.bonus_conversations_left > 0 ? (
                      <>В вашем пакете осталось <span className="font-bold">{boosterBalance.bonus_conversations_left}</span> бонусных диалогов. Они списываются после исчерпания основного лимита.</>
                    ) : subscriptionInfo.usage_percentage >= 95 ? (
                      <>Лимит почти исчерпан. Купите +100 диалогов за €10 — без смены тарифа.</>
                    ) : (
                      <>Использовано {subscriptionInfo.usage_percentage}% месячного лимита. Подстрахуйтесь — купите +100 диалогов за €10.</>
                    )}
                  </p>
                </div>
                <Button
                  onClick={handleBuyBooster}
                  disabled={boosterLoading}
                  className="bg-black text-white hover:bg-gray-800 px-6 py-3 font-bold border-2 border-black whitespace-nowrap"
                  data-testid="buy-booster-btn"
                >
                  {boosterLoading ? '⏳ ...' : '+100 за €10'}
                </Button>
              </div>
            </CardContent>
          </Card>
          )
        )}

        {subscriptionInfo?.payment_failed && (
          <Card className="border-2 border-red-600 bg-red-50 mb-6" data-testid="payment-failed-banner">
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="font-bold text-red-700 mb-1">
                    ⚠️ {t('subscription.paymentFailedTitle') || 'Проблема с оплатой подписки'}
                  </p>
                  <p className="text-sm text-red-700">
                    {t('subscription.paymentFailedText') || 'Банк отклонил списание. Обновите карту, чтобы AI-ассистент продолжил работать без перерыва.'}
                  </p>
                </div>
                <Button
                  onClick={handleManageBilling}
                  className="bg-red-600 text-white hover:bg-red-700 px-6 py-3 font-bold whitespace-nowrap"
                  data-testid="payment-failed-update-card-btn"
                >
                  {t('subscription.updateCardBtn') || 'Обновить карту'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          {!embedded && (() => {
            const isStaffAdmin = user?.role === 'admin';
            const tabVisible = (k) => !isStaffAdmin ? true : (k === 'team' && (adminPerms ? adminPerms.team !== false : true));
            return (
          <TabsList className="grid w-full h-auto grid-cols-4 sm:grid-cols-7 bg-gray-100 text-black border-2 border-black gap-2 p-2 mb-8 rounded-lg">
            {tabVisible('settings') && <TabsTrigger 
              data-testid="settings-tab" 
              value="settings" 
              className="data-[state=active]:bg-black data-[state=active]:text-white bg-white hover:bg-gray-200 border-2 border-gray-300 data-[state=active]:border-black py-3 px-1 sm:px-3 min-h-[52px] flex flex-col sm:flex-row items-center justify-center gap-1 rounded-md transition-all font-medium shadow-sm"
            >
              <span className="text-2xl sm:hidden">⚙️</span>
              <Settings className="w-4 h-4 hidden sm:inline-block md:mr-2" />
              <span className="hidden sm:inline text-xs sm:text-sm md:text-base">{t('tabs.settings')}</span>
            </TabsTrigger>}
            {tabVisible('bookings') && <TabsTrigger 
              data-testid="bookings-tab" 
              value="bookings" 
              className="data-[state=active]:bg-black data-[state=active]:text-white bg-white hover:bg-gray-200 border-2 border-gray-300 data-[state=active]:border-black py-3 px-1 sm:px-3 min-h-[52px] flex flex-col sm:flex-row items-center justify-center gap-1 rounded-md transition-all font-medium shadow-sm"
            >
              <span className="text-2xl sm:hidden">📋</span>
              <History className="w-4 h-4 hidden sm:inline-block md:mr-2" />
              <span className="hidden sm:inline text-xs sm:text-sm md:text-base">{t('tabs.bookings')}</span>
            </TabsTrigger>}
            {tabVisible('clients') && <TabsTrigger 
              data-testid="clients-tab" 
              value="clients" 
              className="data-[state=active]:bg-black data-[state=active]:text-white bg-white hover:bg-gray-200 border-2 border-gray-300 data-[state=active]:border-black py-3 px-1 sm:px-3 min-h-[52px] flex flex-col sm:flex-row items-center justify-center gap-1 rounded-md transition-all font-medium shadow-sm"
            >
              <span className="text-2xl sm:hidden">👥</span>
              <Users className="w-4 h-4 hidden sm:inline-block md:mr-2" />
              <span className="hidden sm:inline text-xs sm:text-sm md:text-base">{t('tabs.clients') || 'Клиенты'}</span>
            </TabsTrigger>}
            {tabVisible('team') && <TabsTrigger 
              data-testid="team-tab" 
              value="team" 
              className="data-[state=active]:bg-black data-[state=active]:text-white bg-white hover:bg-gray-200 border-2 border-gray-300 data-[state=active]:border-black py-3 px-1 sm:px-3 min-h-[52px] flex flex-col sm:flex-row items-center justify-center gap-1 rounded-md transition-all font-medium shadow-sm"
            >
              <span className="text-2xl sm:hidden">🧑‍🤝‍🧑</span>
              <Users className="w-4 h-4 hidden sm:inline-block md:mr-2" />
              <span className="hidden sm:inline text-xs sm:text-sm md:text-base">{t('tabs.team')}</span>
            </TabsTrigger>}
            {tabVisible('subscription') && <TabsTrigger 
              data-testid="subscription-tab" 
              value="subscription" 
              className="data-[state=active]:bg-black data-[state=active]:text-white bg-white hover:bg-gray-200 border-2 border-gray-300 data-[state=active]:border-black py-3 px-1 sm:px-3 min-h-[52px] flex flex-col sm:flex-row items-center justify-center gap-1 rounded-md transition-all font-medium shadow-sm"
            >
              <span className="text-2xl sm:hidden">💳</span>
              <CreditCard className="w-4 h-4 hidden sm:inline-block md:mr-2" />
              <span className="hidden sm:inline text-xs sm:text-sm md:text-base">{t('tabs.subscription') || 'Подписка'}</span>
            </TabsTrigger>}
            {tabVisible('widget') && <TabsTrigger 
              data-testid="widget-tab" 
              value="widget" 
              className="data-[state=active]:bg-black data-[state=active]:text-white bg-white hover:bg-gray-200 border-2 border-gray-300 data-[state=active]:border-black py-3 px-1 sm:px-3 min-h-[52px] flex flex-col sm:flex-row items-center justify-center gap-1 rounded-md transition-all font-medium shadow-sm"
            >
              <span className="text-2xl sm:hidden">💬</span>
              <Code className="w-4 h-4 hidden sm:inline-block md:mr-2" />
              <span className="hidden sm:inline text-xs sm:text-sm md:text-base">{t('tabs.widget')}</span>
            </TabsTrigger>}
            {tabVisible('minisite') && <TabsTrigger 
              data-testid="minisite-tab" 
              value="minisite" 
              className="data-[state=active]:bg-black data-[state=active]:text-white bg-white hover:bg-gray-200 border-2 border-gray-300 data-[state=active]:border-black py-3 px-1 sm:px-3 min-h-[52px] flex flex-col sm:flex-row items-center justify-center gap-1 rounded-md transition-all font-medium shadow-sm"
            >
              <span className="text-2xl sm:hidden">🌐</span>
              <Globe className="w-4 h-4 hidden sm:inline-block md:mr-2" />
              <span className="hidden sm:inline text-xs sm:text-sm md:text-base">{t('tabs.minisite') || 'Mini-site'}</span>
            </TabsTrigger>}
          </TabsList>
            );
          })()}

          <TabsContent value="team" className="mt-0">
            <TeamManagement />
          </TabsContent>

          <TabsContent value="settings" className="mt-0">
            <Card className="bg-white text-black border-2 md:border-4 border-black">
              <CardHeader className="pb-3 md:pb-6">
                <CardTitle className="text-xl md:text-2xl">{t('settings.title')}</CardTitle>
                <CardDescription className="text-gray-600">
                  {t('settings.subtitle')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold border-b-2 border-black pb-2">{t('settings.notifications')}</h3>
                  
                  <div className="bg-gray-50 p-4 rounded-lg border-2 border-gray-300">
                    <h4 className="font-semibold mb-2">{t('settings.emailTitle')}</h4>
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        data-testid="email-notifications-checkbox"
                        type="checkbox"
                        id="email_notifications"
                        checked={settings.email_notifications}
                        onChange={(e) => setSettings({ ...settings, email_notifications: e.target.checked })}
                        className="w-5 h-5 border-2 border-black cursor-pointer"
                      />
                      <Label htmlFor="email_notifications" className="cursor-pointer font-medium">
                        {t('settings.emailCheckbox')} ({user?.email})
                      </Label>
                    </div>
                    {settings.email_notifications && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <Label htmlFor="notification_emails" className="font-medium text-sm">
                          📨 {t('settings.emailRecipients')}
                        </Label>
                        <Input
                          id="notification_emails"
                          type="text"
                          value={settings.notification_emails}
                          onChange={(e) => setSettings({ ...settings, notification_emails: e.target.value })}
                          className="mt-1 border-2 border-black text-black"
                          placeholder={`info@salon.com, director@salon.com  (${t('settings.emailRecipientsDefault', { email: user?.email || 'email' })})`}
                          data-testid="notification-emails-input"
                        />
                        <p className="text-xs text-gray-600 mt-1">
                          {t('settings.emailRecipientsHint')}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Telegram notifications - только для Professional и Unlimited */}
                  {subscriptionInfo?.plan !== 'starter' ? (
                    <div className="bg-gray-50 p-4 rounded-lg border-2 border-gray-300">
                      <h4 className="font-semibold mb-2">{t('settings.telegramTitle')}</h4>
                      <div>
                        <Label htmlFor="chat_id" className="font-medium">{t('settings.chatId')}</Label>
                        <Input
                          data-testid="telegram-chat-id-input"
                          id="chat_id"
                          type="text"
                          value={settings.telegram_chat_id}
                          onChange={(e) => setSettings({ ...settings, telegram_chat_id: e.target.value })}
                          className="mt-1 border-2 border-black text-black"
                          placeholder="-1001234567890"
                        />
                        <div className="mt-2 text-xs bg-blue-50 p-3 rounded border border-blue-200 space-y-2">
                          <p className="font-semibold text-blue-900">📱 {t('settings.chatIdInstructions')}</p>
                          <ol className="list-decimal ml-4 space-y-2 text-gray-700">
                            <li>
                              <span className="font-medium">{t('settings.chatIdStep1')}</span> {t('settings.chatIdStep1Text')} 
                              <span className="font-mono bg-white px-2 py-0.5 mx-1 rounded border border-gray-300">@cebeauty_bot</span>
                            </li>
                            <li>
                              <span className="font-medium">{t('settings.chatIdStep2')}</span> {t('settings.chatIdStep2Text')}
                            </li>
                            <li>
                              <span className="font-medium">{t('settings.chatIdStep3')}</span> {t('settings.chatIdStep3Text')} 
                              <span className="font-mono bg-white px-2 py-0.5 mx-1 rounded border border-gray-300">/start</span>
                            </li>
                            <li>
                              <span className="font-medium">{t('settings.chatIdStep4')}</span> {t('settings.chatIdStep4Text')}
                            </li>
                            <li>
                              <span className="font-medium">{t('settings.chatIdStep5')}</span> {t('settings.chatIdStep5Text')}
                            </li>
                          </ol>
                          <p className="text-xs text-blue-700 mt-2 pt-2 border-t border-blue-200">
                            ✅ {t('settings.chatIdSuccess')}
                          </p>
                        </div>
                      </div>

                      {/* WhatsApp Business — 360Dialog (temporarily hidden per owner request) */}
                      {false && (
                        <div className="mt-4 pt-4 border-t-2 border-dashed border-gray-300">
                          <Whatsapp360Connect API={API} salonId={salon?.id} />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-gray-100 p-4 rounded-lg border-2 border-gray-300 opacity-60">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold">{t('settings.telegramTitle')}</h4>
                        <span className="text-xs bg-yellow-500 text-white px-2 py-1 rounded font-bold">PRO</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        {t('upgrade.telegram.title')}
                      </p>
                      <Button
                        onClick={() => setActiveTab('subscription')}
                        className="bg-blue-600 text-white hover:bg-blue-700 text-sm"
                      >
                        {t('upgrade.telegram.button')}
                      </Button>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold border-b-2 border-black pb-2">{t('settings.businessInfo')}</h3>
                  
                  <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-300">
                    <div className="flex items-start gap-2 mb-3">
                      <div className="text-blue-600 text-2xl">💡</div>
                      <div className="flex-1">
                        <h4 className="font-bold text-blue-900 mb-1">{t('settings.aiKnowledgeTitle')}</h4>
                        <p className="text-sm text-blue-800">
                          {isEcom ? t('settings.aiKnowledgeDescEcom') : t('settings.aiKnowledgeDesc')}
                        </p>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <Label htmlFor="business_type" className="font-medium mb-2 block">
                        🎯 {t('settings.businessType')}
                      </Label>
                      <select
                        id="business_type"
                        value={settings.business_type || 'general'}
                        onChange={(e) => setSettings({ ...settings, business_type: e.target.value })}
                        className="w-full p-3 border-2 border-black rounded-lg text-black"
                      >
                        <option value="general">{t('settings.businessTypes.general')}</option>
                        <option value="medical">{t('settings.businessTypes.medical')}</option>
                        <option value="beauty">{t('settings.businessTypes.beauty')}</option>
                        <option value="ecommerce">{t('settings.businessTypes.ecommerce')}</option>
                        <option value="restaurant">{t('settings.businessTypes.restaurant')}</option>
                        <option value="services">{t('settings.businessTypes.services')}</option>
                      </select>
                      <p className="text-xs text-gray-600 mt-1">
                        💡 {t('settings.businessTypeHint')}
                      </p>
                    </div>
                    
                    {settings.business_type === 'ecommerce' && (
                      <TildaMcpConnect API={API} />
                    )}
                    {settings.business_type === 'ecommerce' && (
                      <div className="mb-4">
                        <Label htmlFor="product_feed_url" className="font-medium mb-2 block">
                          📦 {t('settings.productFeedUrl')}
                        </Label>
                        <input
                          type="url"
                          id="product_feed_url"
                          value={settings.product_feed_url || ''}
                          onChange={(e) => setSettings({ ...settings, product_feed_url: e.target.value })}
                          className="w-full p-3 border-2 border-black rounded-lg text-black"
                          placeholder="https://yourstore.com/products.xml"
                        />
                        <p className="text-xs text-gray-600 mt-1">
                          💡 {t('settings.productFeedHint')}
                        </p>
                      </div>
                    )}
                    
                    <Label htmlFor="salon_info" className="font-medium mb-2 block">
                      {t('settings.knowledgeBase')}
                    </Label>
                    
                    <textarea
                      id="salon_info"
                      value={settings.salon_info || ''}
                      onChange={(e) => setSettings({ ...settings, salon_info: e.target.value })}
                      className="w-full min-h-[200px] p-3 border-2 border-black rounded-lg text-black font-mono text-sm"
                      placeholder={t('settings.knowledgeBasePlaceholder')}
                    />
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-gray-600">
                        💡 {t('settings.knowledgeTip')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {settings.salon_info?.length || 0} / 10,000 символов
                      </p>
                    </div>

                    <Label htmlFor="job_reply" className="font-medium mb-2 block mt-4">
                      💼 {t('settings.jobReply')}
                    </Label>
                    <textarea
                      id="job_reply"
                      data-testid="job-reply-input"
                      value={settings.job_reply || ''}
                      onChange={(e) => setSettings({ ...settings, job_reply: e.target.value })}
                      className="w-full min-h-[70px] p-3 border-2 border-black rounded-lg text-black text-sm"
                      placeholder={t('settings.jobReplyPlaceholder')}
                    />
                    <p className="text-xs text-gray-600 mt-1">{t('settings.jobReplyTip')}</p>

                    {/* Import services & prices from CRM link (Planity/Booksy/Fresha/any site) */}
                    <div className={`mt-6 pt-4 border-t-2 border-gray-200 ${isEcom ? 'hidden' : ''}`} data-testid="crm-import-block">
                      <h4 className="font-bold text-black mb-1">🔗 {t('settings.crmImportTitle')}</h4>
                      <p className="text-xs text-gray-600 mb-3">{t('settings.crmImportHint')}</p>
                      <div className="flex gap-2 items-center">
                        <Input
                          data-testid="crm-url-input"
                          value={crmUrl}
                          onChange={(e) => setCrmUrl(e.target.value)}
                          placeholder="https://planity.com/votre-salon  ·  booksy.com/...  ·  fresha.com/..."
                          className="border-2 border-black"
                        />
                        <Button
                          data-testid="crm-preview-btn"
                          onClick={handleCrmPreview}
                          disabled={crmLoading || !crmUrl.trim()}
                          className="bg-black text-white hover:bg-gray-800 whitespace-nowrap shrink-0"
                        >
                          {crmLoading ? '⏳' : `🔍 ${t('settings.crmImportFetch')}`}
                        </Button>
                      </div>

                      {crmPreview && crmPreview.length > 0 && (
                        <div className="mt-3 border-2 border-black rounded-lg overflow-hidden" data-testid="crm-preview-table">
                          <div className="bg-gray-100 px-3 py-2 flex items-center justify-between">
                            <span className="text-sm font-semibold">{t('settings.crmImportFound', { count: crmPreview.length })}</span>
                            <span className="text-xs text-gray-600">{crmSelected.size} / {crmPreview.length}</span>
                          </div>
                          <div className="max-h-64 overflow-y-auto divide-y">
                            {crmPreview.map((s, i) => (
                              <label key={i} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50" data-testid={`crm-service-row-${i}`}>
                                <input
                                  type="checkbox"
                                  checked={crmSelected.has(i)}
                                  onChange={(e) => {
                                    const next = new Set(crmSelected);
                                    e.target.checked ? next.add(i) : next.delete(i);
                                    setCrmSelected(next);
                                  }}
                                  className="w-4 h-4 accent-black"
                                />
                                <span className="flex-1 text-sm text-black truncate">{s.name}</span>
                                <span className="text-sm font-semibold text-black shrink-0">{s.price}{settings.currency_symbol || '€'}</span>
                              </label>
                            ))}
                          </div>
                          <div className="p-3 bg-gray-50 border-t">
                            <Button
                              data-testid="crm-confirm-btn"
                              onClick={handleCrmConfirm}
                              disabled={crmImporting || crmSelected.size === 0}
                              className="w-full bg-emerald-600 text-white hover:bg-emerald-700 font-semibold"
                            >
                              {crmImporting ? '⏳ …' : `⬇ ${t('settings.crmImportConfirm', { count: crmSelected.size })}`}
                            </Button>
                            <p className="text-xs text-gray-600 mt-2">{t('settings.crmImportNote')}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Revenue tracking (Task 1) */}
                    <div className={`mt-6 pt-4 border-t-2 border-gray-200 ${isEcom ? 'hidden' : ''}`} data-testid="revenue-block">
                      <h4 className="font-bold text-black mb-1">💰 {t('settings.revenueTitle')}</h4>
                      <p className="text-xs text-gray-600 mb-3">{t('settings.revenueHint')}</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="avg_ticket" className="font-medium text-sm">{t('settings.avgTicket')}</Label>
                          <Input
                            id="avg_ticket"
                            data-testid="avg-ticket-input"
                            type="number" min="0" step="1"
                            value={settings.avg_ticket}
                            onChange={(e) => setSettings({ ...settings, avg_ticket: e.target.value })}
                            className="mt-1 border-2 border-black"
                            placeholder="50"
                          />
                        </div>
                        <div>
                          <Label htmlFor="currency_symbol" className="font-medium text-sm">{t('settings.currency')}</Label>
                          <Input
                            id="currency_symbol"
                            data-testid="currency-input"
                            value={settings.currency_symbol}
                            onChange={(e) => setSettings({ ...settings, currency_symbol: e.target.value.slice(0, 4) })}
                            className="mt-1 border-2 border-black"
                            placeholder="€"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Interface language (moved from header) */}
                    <div className="mt-6 pt-4 border-t-2 border-gray-200">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <h4 className="font-bold text-black mb-1">🌐 {t('settings.languageTitle') !== 'settings.languageTitle' ? t('settings.languageTitle') : 'Язык интерфейса'}</h4>
                        </div>
                        <LanguageSwitcher />
                      </div>
                    </div>

                    {/* Operating mode: autopilot / manual / crm_only */}
                    <div className="mt-6 pt-4 border-t-2 border-gray-200">
                      <h4 className={`font-bold text-black mb-1 ${isEcom ? 'hidden' : ''}`}>⚙️ {t('settings.modeTitle')}</h4>
                      <p className={`text-xs text-gray-600 mb-3 ${isEcom ? 'hidden' : ''}`}>{t('settings.modeHint')}</p>
                      <div className={`grid grid-cols-1 gap-2 ${isEcom ? 'hidden' : ''}`} data-testid="operating-mode-selector">
                        {[
                          { id: "autopilot", icon: "🤖", title: t('settings.modeAutopilot'), desc: t('settings.modeAutopilotDesc') },
                          { id: "manual", icon: "📅", title: t('settings.modeManual'), desc: t('settings.modeManualDesc') },
                          { id: "crm_only", icon: "📇", title: t('settings.modeCrmOnly'), desc: t('settings.modeCrmOnlyDesc') },
                        ].map((m) => {
                          const active = (settings.operating_mode || "autopilot") === m.id;
                          return (
                            <button
                              key={m.id}
                              type="button"
                              data-testid={`operating-mode-${m.id}`}
                              onClick={() => setSettings({ ...settings, operating_mode: m.id, calendar_enabled: m.id !== "crm_only" })}
                              className={`text-left p-3 rounded-xl border-2 transition-all ${active ? "border-black bg-black text-white" : "border-gray-200 bg-white text-black hover:border-gray-400"}`}
                            >
                              <div className="flex items-center gap-2 font-bold">
                                <span>{m.icon}</span><span>{m.title}</span>
                                {active && <span className="ml-auto text-xs">✓</span>}
                              </div>
                              <p className={`text-xs mt-1 ${active ? "text-gray-200" : "text-gray-500"}`}>{m.desc}</p>
                            </button>
                          );
                        })}
                      </div>
                      <div className="mt-4 pt-3 border-t border-gray-200" data-testid="manual-pause-block">
                        <h4 className="font-bold text-black mb-1 text-sm">{t('settings.manualPauseTitle')}</h4>
                        <p className="text-xs text-gray-600 mb-2">{t('settings.manualPauseHint')}</p>
                        <select
                          data-testid="manual-pause-select"
                          value={String(settings.manual_pause_hours ?? 6)}
                          onChange={(e) => setSettings({ ...settings, manual_pause_hours: parseInt(e.target.value, 10) })}
                          className="w-full sm:w-64 border-2 border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:border-black outline-none"
                        >
                          {[1, 6, 24, 72, 0].map((h) => (
                            <option key={h} value={String(h)}>{t(`settings.manualPause${h}`)}</option>
                          ))}
                        </select>
                      </div>
                      <div className="mt-4 pt-3 border-t border-gray-200" data-testid="human-first-block">
                        <h4 className="font-bold text-black mb-1 text-sm">{t('settings.humanFirstTitle')}</h4>
                        <p className="text-xs text-gray-600 mb-2">{t('settings.humanFirstHint')}</p>
                        <select
                          data-testid="human-first-select"
                          value={String(settings.ai_reply_delay_sec ?? 0)}
                          onChange={(e) => setSettings({ ...settings, ai_reply_delay_sec: parseInt(e.target.value, 10) })}
                          className="w-full sm:w-64 border-2 border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:border-black outline-none"
                        >
                          {[0, 30, 60, 120, 300, 600].map((sec) => (
                            <option key={sec} value={String(sec)}>{t(`settings.humanFirst${sec}`)}</option>
                          ))}
                        </select>
                      </div>
                      <AiScheduleEditor value={settings.ai_schedule} timezone={salon?.timezone}
                        onTimezoneChange={(tz) => setSalon((s) => ({ ...s, timezone: tz }))}
                        onChange={(v) => setSettings({ ...settings, ai_schedule: v })} />
                      <VoiceReplySettings />
                    </div>

                    {/* Branding: hide Chatity + custom AI introduction */}
                    <div className="mt-6 pt-4 border-t-2 border-gray-200">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <h4 className="font-bold text-black mb-1">✨ {t('settings.brandingTitle')}</h4>
                          <p className="text-xs text-gray-600">{t('settings.brandingHint')}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                          <input
                            type="checkbox"
                            data-testid="hide-branding-toggle"
                            checked={settings.hide_chatity_branding}
                            onChange={(e) => setSettings({ ...settings, hide_chatity_branding: e.target.checked })}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-300 peer-checked:bg-black rounded-full peer transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
                        </label>
                      </div>
                      <div className="mt-3">
                        <label className="text-sm font-semibold text-black">{t('settings.introLabel')}</label>
                        <textarea
                          data-testid="ai-intro-input"
                          value={settings.ai_intro_text || ""}
                          onChange={(e) => setSettings({ ...settings, ai_intro_text: e.target.value })}
                          className="w-full min-h-[60px] p-3 border-2 border-black rounded-lg text-black text-sm mt-1"
                          placeholder={t('settings.introPlaceholder')}
                        />
                        <p className="text-xs text-gray-500 mt-1">🌐 {t('settings.introHint')}</p>
                      </div>
                    </div>

                    {/* Reactivation moved to the Marketing tab */}
                  </div>
                </div>

                {/* Кастомизация виджета - только для Professional и Unlimited */}
                {subscriptionInfo?.plan !== 'starter' ? (
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold border-b-2 border-black pb-2">{t('settings.aiChatbot')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="primary_color" className="font-medium">{t('settings.primaryColor')}</Label>
                        <Input
                          data-testid="primary-color-input"
                          id="primary_color"
                          type="color"
                          value={settings.widget_settings.primary_color}
                          onChange={(e) => setSettings({
                            ...settings,
                            widget_settings: { ...settings.widget_settings, primary_color: e.target.value }
                          })}
                          className="mt-1 h-12 border-2 border-black"
                        />
                      </div>
                      <div>
                        <Label htmlFor="secondary_color" className="font-medium">{t('settings.secondaryColor')}</Label>
                        <Input
                          data-testid="secondary-color-input"
                          id="secondary_color"
                          type="color"
                          value={settings.widget_settings.secondary_color}
                          onChange={(e) => setSettings({
                            ...settings,
                            widget_settings: { ...settings.widget_settings, secondary_color: e.target.value }
                          })}
                          className="mt-1 h-12 border-2 border-black"
                        />
                      </div>
                      <div>
                        <Label htmlFor="button_color" className="font-medium">{t('settings.buttonColor')}</Label>
                        <Input
                          data-testid="button-color-input"
                          id="button_color"
                          type="color"
                          value={settings.widget_settings.button_color}
                          onChange={(e) => setSettings({
                            ...settings,
                            widget_settings: { ...settings.widget_settings, button_color: e.target.value }
                          })}
                          className="mt-1 h-12 border-2 border-black"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="bot_name" className="font-medium">{t('settings.botName')}</Label>
                      <Input
                        data-testid="bot-name-input"
                        id="bot_name"
                        type="text"
                        value={settings.widget_settings.bot_name}
                        onChange={(e) => setSettings({
                          ...settings,
                          widget_settings: { ...settings.widget_settings, bot_name: e.target.value }
                        })}
                        className="mt-1 border-2 border-black text-black"
                        placeholder={t('settings.defaultBotName')}
                      />
                    </div>
                    <div>
                      <Label htmlFor="welcome_message" className="font-medium">{t('settings.welcomeMessage')}</Label>
                      <Textarea
                        data-testid="welcome-message-input"
                        id="welcome_message"
                        value={settings.widget_settings.welcome_message}
                        onChange={(e) => setSettings({
                          ...settings,
                          widget_settings: { ...settings.widget_settings, welcome_message: e.target.value }
                        })}
                        className="mt-1 border-2 border-black min-h-20"
                        placeholder={t('settings.defaultWelcomeMessage')}
                      />
                    </div>

                    {/* Balloon Settings */}
                    <div className="border-t-2 border-gray-200 pt-4 mt-6">
                      <h4 className="font-semibold text-lg mb-3">💬 {t('settings.balloonSettings') || 'Всплывающая подсказка'}</h4>
                      
                      {/* Balloon Enabled */}
                      <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 rounded-lg">
                        <div>
                          <Label className="font-medium">{t('settings.balloonEnabled') || 'Показывать подсказку'}</Label>
                          <p className="text-xs text-gray-500">{t('settings.balloonEnabledDesc') || 'Всплывающее сообщение привлекает внимание'}</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={settings.widget_settings.balloon_enabled !== false}
                          onChange={(e) => setSettings({
                            ...settings,
                            widget_settings: { ...settings.widget_settings, balloon_enabled: e.target.checked }
                          })}
                          className="w-5 h-5"
                        />
                      </div>
                      
                      {/* Balloon Message */}
                      <div className="mb-4">
                        <Label htmlFor="balloon_message" className="font-medium">{t('settings.balloonMessage') || 'Текст подсказки'}</Label>
                        <Input
                          id="balloon_message"
                          type="text"
                          value={settings.widget_settings.balloon_message || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            widget_settings: { ...settings.widget_settings, balloon_message: e.target.value }
                          })}
                          className="mt-1 border-2 border-black text-black"
                          placeholder={t('settings.balloonMessagePlaceholder') || 'Здравствуйте! Помогу записаться на услугу 👋'}
                        />
                      </div>
                      
                      {/* Balloon Delay */}
                      <div className="mb-4">
                        <Label htmlFor="balloon_delay" className="font-medium">{t('settings.balloonDelay') || 'Задержка (секунды)'}</Label>
                        <Input
                          id="balloon_delay"
                          type="number"
                          min="1"
                          max="30"
                          value={settings.widget_settings.balloon_delay || 5}
                          onChange={(e) => setSettings({
                            ...settings,
                            widget_settings: { ...settings.widget_settings, balloon_delay: parseInt(e.target.value) || 5 }
                          })}
                          className="mt-1 border-2 border-black text-black w-32"
                        />
                        <p className="text-xs text-gray-500 mt-1">{t('settings.balloonDelayDesc') || 'Через сколько секунд показать'}</p>
                      </div>
                      
                      {/* Auto Open Settings */}
                      <div className="border-t border-gray-200 pt-4 mt-4">
                        <div className="flex items-center justify-between mb-4 p-3 bg-blue-50 rounded-lg">
                          <div>
                            <Label className="font-medium">{t('settings.autoOpenEnabled') || 'Автооткрытие чата'}</Label>
                            <p className="text-xs text-gray-500">{t('settings.autoOpenEnabledDesc') || 'Автоматически открыть чат если не кликнули'}</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={settings.widget_settings.auto_open_enabled || false}
                            onChange={(e) => setSettings({
                              ...settings,
                              widget_settings: { ...settings.widget_settings, auto_open_enabled: e.target.checked }
                            })}
                            className="w-5 h-5"
                          />
                        </div>
                        
                        {settings.widget_settings.auto_open_enabled && (
                          <div>
                            <Label htmlFor="auto_open_delay" className="font-medium">{t('settings.autoOpenDelay') || 'Задержка автооткрытия (секунды)'}</Label>
                            <Input
                              id="auto_open_delay"
                              type="number"
                              min="5"
                              max="60"
                              value={settings.widget_settings.auto_open_delay || 15}
                              onChange={(e) => setSettings({
                                ...settings,
                                widget_settings: { ...settings.widget_settings, auto_open_delay: parseInt(e.target.value) || 15 }
                              })}
                              className="mt-1 border-2 border-black text-black w-32"
                            />
                            <p className="text-xs text-gray-500 mt-1">{t('settings.autoOpenDelayDesc') || 'Через сколько секунд автооткрыть'}</p>
                          </div>
                        )}
                      </div>
                    </div>

                  </div>
                ) : (
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold border-b-2 border-black pb-2">{t('settings.aiChatbot')}</h3>
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg border-2 border-blue-300 relative overflow-hidden">
                      <div className="absolute top-4 right-4">
                        <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-bold">PRO</span>
                      </div>
                      <h4 className="text-lg font-bold text-black mb-2">
                        🎨 {t('upgrade.widget.title')}
                      </h4>
                      <p className="text-gray-700 mb-4">
                        {t('upgrade.widget.description')}
                      </p>
                      <ul className="space-y-2 mb-4 text-sm">
                        <li className="flex items-center gap-2">
                          <span className="text-green-600">✓</span>
                          <span>{t('upgrade.widget.feature1')}</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-green-600">✓</span>
                          <span>{t('upgrade.widget.feature2')}</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-green-600">✓</span>
                          <span>{t('upgrade.widget.feature3')}</span>
                        </li>
                      </ul>
                      <Button
                        onClick={() => setActiveTab('subscription')}
                        className="bg-blue-600 text-white hover:bg-blue-700 font-bold"
                      >
                        {t('upgrade.widget.button')}
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    onClick={handleTestNotifications}
                    data-testid="test-notifications-btn"
                    variant="outline"
                    className="flex-1 border-2 border-blue-600 text-blue-600 hover:bg-blue-50 py-6 text-lg font-medium"
                  >
                    🧪 Тест уведомлений
                  </Button>
                  <Button
                    data-testid="save-settings-button"
                    onClick={handleSaveSettings}
                    disabled={saving}
                    className="flex-1 bg-black text-white hover:bg-gray-800 border-2 border-black py-6 text-lg font-medium disabled:opacity-70"
                  >
                    {saving && <Loader2 className="w-5 h-5 mr-2 animate-spin" />}
                    {saving ? (t('settings.saving') || '…') : t('settings.saveButton')}
                  </Button>
                </div>
                <PrivacySettings API={API} salon={salon} onSaved={setSalon} />
                {user?.role !== 'admin' && <DeleteAccountLink API={API} />}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="marketing" className="mt-0">
            <MarketingTab API={API} t={t} />
          </TabsContent>

          <TabsContent value="analytics" className="mt-0">
            <AnalyticsTab />
          </TabsContent>

          <TabsContent value="bookings" className="mt-0">
            {subscriptionInfo?.plan === 'starter' ? (
              // Starter Plan - только список заявок
              <Card className="bg-white text-black border-2 md:border-4 border-black">
                <CardHeader>
                  <CardTitle className="text-2xl">{t('bookings.title') || 'История заявок'}</CardTitle>
                  <CardDescription className="text-gray-600" data-testid="starter-bookings-counters">
                    {t('bookings.totalCount') || 'Всего заявок'}: {bookings.length} • {t('bookings.uniqueClients') || 'Уникальных клиентов'}: {countUniqueClients(bookings)}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* History limit banner (Starter) */}
                  <div data-testid="history-limit-banner" className="mb-4 p-4 bg-amber-50 border-2 border-amber-400 rounded-lg flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-sm font-medium">
                      🔒 {t('bookings.historyLimitBanner') || 'Заявки старше 30 дней доступны на тарифе Professional'}
                    </p>
                    <Button
                      data-testid="history-limit-upgrade-btn"
                      size="sm"
                      onClick={() => setActiveTab('subscription')}
                      className="bg-black text-white hover:bg-gray-800"
                    >
                      {t('analytics.upgradeButton') || 'Обновить план'}
                    </Button>
                  </div>
                  {bookings.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      {t('bookings.noBookings') || 'Пока нет заявок'}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {bookings.map((booking, index) => (
                        <div key={index} className="p-4 border-2 border-black rounded-lg bg-white">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-bold text-lg">
                                {booking.client_name}
                                {isRepeatBooking(booking, bookings) && (
                                  <span data-testid={`starter-repeat-badge-${index}`} className="ml-2 inline-flex items-center px-2 py-0.5 bg-orange-100 text-orange-700 border border-orange-300 rounded-full text-xs font-semibold align-middle">
                                    🔁 {t('bookings.repeatBadge') || 'Повторная'}
                                  </span>
                                )}
                              </p>
                              <p className="text-sm text-gray-600">{booking.phone || booking.client_phone}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-600">{new Date(booking.created_at).toLocaleDateString()}</p>
                              <p className="text-xs text-gray-500">{new Date(booking.created_at).toLocaleTimeString()}</p>
                            </div>
                          </div>
                          {booking.service && <p className="text-sm"><strong>{t('bookings.service')}:</strong> {booking.service}</p>}
                          {(booking.preferred_date || booking.date) && (
                            <p className="text-sm">
                              <strong>{t('bookings.preferredDate')}:</strong> {booking.preferred_date || booking.date}
                              {booking.time && ` ${booking.time}`}
                            </p>
                          )}
                          {booking.preferred_time && <p className="text-sm"><strong>{t('bookings.preferredTime')}:</strong> {booking.preferred_time}</p>}
                          {booking.branch_name && <p className="text-sm" data-testid={`booking-branch-${index}`}>📍 <strong>{booking.branch_name}</strong></p>}
                          {booking.staff_name && <p className="text-sm" data-testid={`booking-master-${index}`}>👤 <strong>{booking.staff_name}</strong>{booking.slot_confirmed && booking.start_at && <span className="ml-2 text-green-700 font-semibold">✅ {booking.start_at.replace('T', ' ')}</span>}</p>}
                          <p className="text-sm mt-1"><ChannelBadge booking={booking} index={index} /></p>
                          {booking.reschedule_requested && (
                            <div className="mt-2 p-2 border-2 border-orange-300 bg-orange-50 rounded-lg" data-testid={`booking-reschedule-${index}`}>
                              <p className="text-sm text-orange-700 font-semibold mb-2">🔁 {booking.reschedule_note ? `«${booking.reschedule_note}»` : ''}</p>
                              <div className="flex flex-wrap gap-2 items-center">
                                <input
                                  data-testid={`reschedule-date-${index}`}
                                  placeholder="09.09"
                                  defaultValue=""
                                  id={`rs-date-${booking.id}`}
                                  className="border-2 border-black rounded px-2 py-1 text-sm w-24"
                                />
                                <input
                                  data-testid={`reschedule-time-${index}`}
                                  placeholder="12:00"
                                  id={`rs-time-${booking.id}`}
                                  className="border-2 border-black rounded px-2 py-1 text-sm w-20"
                                />
                                <Button
                                  size="sm"
                                  data-testid={`reschedule-confirm-${index}`}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  onClick={() => resolveReschedule(booking.id, 'confirm')}
                                >
                                  ✅ {t('bookings.confirmReschedule') !== 'bookings.confirmReschedule' ? t('bookings.confirmReschedule') : 'Подтвердить перенос'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  data-testid={`reschedule-decline-${index}`}
                                  className="border-2 border-red-400 text-red-600 hover:bg-red-50"
                                  onClick={() => resolveReschedule(booking.id, 'decline')}
                                >
                                  ✖
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Upgrade Banner */}
                  <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border-2 border-blue-300 relative overflow-hidden">
                    <div className="relative z-10">
                      <h3 className="text-xl font-bold text-black mb-2">
                        📊 {t('analytics.upgradeToPro') || 'Расширенная аналитика'}
                      </h3>
                      <p className="text-gray-700 mb-4">
                        {t('analytics.upgradeDescription') || 'Обновите план до Professional или Unlimited для доступа к:'}
                      </p>
                      <ul className="space-y-2 mb-4 text-sm">
                        <li className="flex items-center gap-2">
                          <span className="text-green-600">✓</span>
                          <span>{t('analytics.feature1') || 'Графики и статистика'}</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-green-600">✓</span>
                          <span>{t('analytics.feature2') || 'UTM метки и источники трафика'}</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-green-600">✓</span>
                          <span>{t('analytics.feature3') || 'Фильтры по датам'}</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-green-600">✓</span>
                          <span>{t('analytics.feature4') || 'Анализ конверсии'}</span>
                        </li>
                      </ul>
                      <Button
                        onClick={() => setActiveTab('subscription')}
                        className="bg-blue-600 text-white hover:bg-blue-700 font-bold"
                      >
                        {t('analytics.upgradeButton') || 'Обновить план'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              // Professional & Unlimited - полная аналитика
              <BookingsAnalytics
                bookings={bookings}
                analytics={analytics}
                dateFilter={dateFilter}
                setDateFilter={setDateFilter}
                customStartDate={customStartDate}
                setCustomStartDate={setCustomStartDate}
                customEndDate={customEndDate}
                setCustomEndDate={setCustomEndDate}
                onRefresh={() => fetchData()}
                subscriptionInfo={subscriptionInfo}
              />
            )}
          </TabsContent>

          <TabsContent value="clients" className="mt-0">
            <ClientsList />
          </TabsContent>

          <TabsContent value="subscription" className="mt-0">
            {localStorage.getItem("referral_banner_hidden") === "1" && <ReferralBanner lang={lang} alwaysShow />}
            <Card className="bg-white text-black border-4 border-black">
              <CardHeader>
                <CardTitle className="text-2xl">💳 {t('subscription.title')}</CardTitle>
                <CardDescription className="text-gray-600">
                  {t('subscription.subtitle')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Current Plan Info */}
                {subscriptionInfo && (
                  <div className="mb-8 p-6 bg-gray-50 rounded-lg border-2 border-gray-300">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-bold mb-2">
                          {subscriptionInfo.is_paid ? `✅ ${t('subscription.currentPlan')}` : `🎁 ${t('subscription.trialPeriod')}`}
                        </h3>
                        <p className="text-2xl font-bold uppercase mb-2">{subscriptionInfo.plan}</p>
                        {!subscriptionInfo.is_paid && (
                          <p className="text-sm text-gray-600">
                            {subscriptionInfo.trial_days_left > 0 
                              ? `${subscriptionInfo.trial_days_left} ${subscriptionInfo.trial_days_left === 1 ? t('subscription.day') : t('subscription.days')} ${t('subscription.daysRemaining')}`
                              : t('subscription.expired')}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600 mb-1">{t('subscription.usage')}</p>
                        <p className="text-2xl font-bold">
                          {subscriptionInfo.conversations_used} / {subscriptionInfo.is_unlimited ? '∞' : subscriptionInfo.conversations_limit}
                        </p>
                        {!subscriptionInfo.is_unlimited && (
                          <div className="mt-2 w-48">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-full rounded-full ${
                                  subscriptionInfo.usage_percentage >= 80 ? 'bg-red-500' : 
                                  subscriptionInfo.usage_percentage >= 50 ? 'bg-yellow-500' : 
                                  'bg-green-500'
                                }`}
                                style={{ width: `${subscriptionInfo.usage_percentage}%` }}
                              />
                            </div>
                            <p className="text-xs text-gray-600 mt-1">{subscriptionInfo.usage_percentage}% {t('subscription.used')}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Add-ons: dialog packs, extra channels, extra branches */}
                <div className="mb-8 p-6 bg-white rounded-lg border-2 border-black" data-testid="addons-section">
                  <h3 className="text-lg font-bold mb-1">🧩 {t('addons.title')}</h3>
                  <p className="text-sm text-gray-600 mb-4">{t('addons.subtitle')}</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Dialog packs */}
                    <div className="border-2 border-gray-300 rounded-lg p-4 flex flex-col">
                      <div className="font-bold">💬 {t('addons.dialogs')}</div>
                      <p className="text-xs text-gray-600 mt-1 flex-1">{t('addons.dialogsHint')}</p>
                      <div className="flex items-center gap-2 mt-3">
                        <button data-testid="booster-qty-minus" onClick={() => setBoosterQty(Math.max(1, boosterQty - 1))}
                          className="w-8 h-8 border-2 border-black rounded font-bold">−</button>
                        <span className="font-bold w-16 text-center" data-testid="booster-qty">{boosterQty * 100}</span>
                        <button data-testid="booster-qty-plus" onClick={() => setBoosterQty(Math.min(10, boosterQty + 1))}
                          className="w-8 h-8 border-2 border-black rounded font-bold">+</button>
                      </div>
                      <Button onClick={handleBuyBooster} disabled={boosterLoading} data-testid="addon-buy-dialogs-btn"
                        className="mt-3 bg-black text-white font-semibold">
                        {boosterLoading ? '⏳' : `${t('addons.buy')} — €${(boosterQty * 10).toFixed(0)}`}
                      </Button>
                      {addonsInfo?.bonus_conversations_left > 0 && (
                        <p className="text-xs text-green-700 mt-2">💎 {t('addons.active')}: +{addonsInfo.bonus_conversations_left}</p>
                      )}
                    </div>
                    {/* Extra channel */}
                    <div className="border-2 border-gray-300 rounded-lg p-4 flex flex-col">
                      <div className="font-bold">📡 {t('addons.channel')}</div>
                      <p className="text-xs text-gray-600 mt-1 flex-1">{t('addons.channelHint')}</p>
                      {addonsInfo && (
                        <p className="text-xs text-gray-700 mt-2">
                          {t('addons.connected')}: <b>{addonsInfo.channels.used}</b> / {addonsInfo.channels.allowed === -1 ? '∞' : addonsInfo.channels.allowed}
                        </p>
                      )}
                      <Button onClick={() => handleBuyAddon('channel')} disabled={addonLoading === 'channel'} data-testid="addon-buy-channel-btn"
                        className="mt-3 bg-black text-white font-semibold">
                        {addonLoading === 'channel' ? '⏳' : `${t('addons.buy')} — €${addonsInfo?.prices?.channel ?? 10}/${t('addons.mo')}`}
                      </Button>
                    </div>
                    {/* Extra branch */}
                    <div className="border-2 border-gray-300 rounded-lg p-4 flex flex-col">
                      <div className="font-bold">🏢 {t('addons.branch')}</div>
                      <p className="text-xs text-gray-600 mt-1 flex-1">{t('addons.branchHint')}</p>
                      {addonsInfo && (
                        <p className="text-xs text-gray-700 mt-2">
                          {t('addons.branches')}: <b>{addonsInfo.branches.used}</b> / {addonsInfo.branches.allowed === -1 ? '∞' : addonsInfo.branches.allowed}
                        </p>
                      )}
                      <Button onClick={() => handleBuyAddon('branch')} disabled={addonLoading === 'branch'} data-testid="addon-buy-branch-btn"
                        className="mt-3 bg-black text-white font-semibold">
                        {addonLoading === 'branch' ? '⏳' : `${t('addons.buy')} — €${addonsInfo?.prices?.branch ?? ''}/${t('addons.mo')}`}
                      </Button>
                    </div>
                  </div>
                  <ActiveAddonsList items={addonsInfo?.items} onChanged={fetchBoosterBalance} />
                </div>

                {/* <div className="flex justify-center mb-6">
                  <div className="bg-blue-50 border-2 border-blue-500 rounded-lg p-4 inline-block">
                    <div className="text-center mb-2">
                      <span className="text-sm font-semibold text-gray-700">
                        {paymentSystem === 'tinkoff' ? '🇷🇺 Оплата:' : '🌍 Payment:'}
                      </span>
                    </div>
                    <div className="inline-flex bg-white rounded-lg border-2 border-blue-500 p-1">
                      <button
                        onClick={() => {
                          setPaymentSystem('tinkoff');
                          console.log('🇷🇺 Switched to Tinkoff (RUB)');
                        }}
                        className={`px-6 py-2 rounded-md font-semibold transition-all ${
                          paymentSystem === 'tinkoff'
                            ? 'bg-blue-500 text-white'
                            : 'text-gray-700 hover:text-black'
                        }`}
                      >
                        Tinkoff (₽)
                      </button>
                      <button
                        onClick={() => {
                          setPaymentSystem('stripe');
                          console.log('🌍 Switched to Stripe (EUR)');
                        }}
                        className={`px-6 py-2 rounded-md font-semibold transition-all ${
                          paymentSystem === 'stripe'
                            ? 'bg-blue-500 text-white'
                            : 'text-gray-700 hover:text-black'
                        }`}
                      >
                        Stripe (€)
                      </button>
                    </div>
                    <div className="text-xs text-gray-600 text-center mt-2">
                      {paymentSystem === 'tinkoff' 
                        ? 'Для российских карт' 
                        : 'For international cards'}
                    </div>
                  </div>
                </div> */}

                {/* Billing Period Toggle */}
                <div className="flex justify-center mb-6">
                  <div className="inline-flex bg-gray-100 rounded-lg border-2 border-black p-1">
                    <button
                      onClick={() => setBillingPeriod('monthly')}
                      className={`px-6 py-3 rounded-md font-semibold transition-all ${
                        billingPeriod === 'monthly'
                          ? 'bg-black text-white'
                          : 'text-gray-700 hover:text-black'
                      }`}
                    >
                      {t('subscription.monthly') || 'Ежемесячно'}
                    </button>
                    <button
                      onClick={() => setBillingPeriod('yearly')}
                      className={`px-6 py-3 rounded-md font-semibold transition-all relative ${
                        billingPeriod === 'yearly'
                          ? 'bg-black text-white'
                          : 'text-gray-700 hover:text-black'
                      }`}
                    >
                      {t('subscription.yearly') || 'Ежегодно'}
                      <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                        -17%
                      </span>
                    </button>
                  </div>
                </div>

                {/* Promocode + Affiliate Section */}
                <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto mb-8">
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-500 rounded-lg p-4 w-full flex flex-col">
                    <div className="text-center mb-3">
                      <span className="text-lg font-bold">🎫 {t('promocode.title')}</span>
                    </div>
                    
                    {!promocodeApplied ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={promocodeInput}
                          onChange={(e) => setPromocodeInput(e.target.value.toUpperCase())}
                          placeholder={t('promocode.placeholder')}
                          className="flex-1 min-w-0 px-4 py-2 border-2 border-purple-500 rounded-md uppercase font-semibold"
                          onKeyPress={(e) => e.key === 'Enter' && validatePromocode()}
                        />
                        <Button
                          onClick={validatePromocode}
                          className="bg-purple-600 hover:bg-purple-700 text-white font-semibold"
                        >
                          {t('promocode.apply')}
                        </Button>
                      </div>
                    ) : (
                      <div className="bg-green-100 border-2 border-green-500 rounded-lg p-3 flex items-center justify-between gap-2 flex-wrap">
                        <div>
                          <span className="text-green-800 font-bold text-lg">{promocodeApplied.code}</span>
                          {promocodeApplied.promo_type === 'free_months' ? (
                            <span className="ml-2 bg-amber-500 text-white px-2 py-1 rounded-full text-sm font-semibold">
                              🎁 {promocodeApplied.free_months} {t('promocode.freeMonths') || 'мес. бесплатно'}
                            </span>
                          ) : (
                            <span className="ml-2 bg-green-500 text-white px-2 py-1 rounded-full text-sm font-semibold">
                              -{promocodeApplied.discount_percent}% {t('promocode.discount')}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {promocodeApplied.promo_type === 'free_months' && (
                            <Button
                              onClick={redeemPromocode}
                              className="bg-amber-500 hover:bg-amber-600 text-white font-semibold"
                              data-testid="promo-redeem-btn"
                            >
                              {t('promocode.activate') || 'Активировать'}
                            </Button>
                          )}
                          <Button
                            onClick={clearPromocode}
                            variant="ghost"
                            className="text-red-600 hover:text-red-700"
                            title={t('promocode.remove')}
                          >
                            ✕
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {promocodeError && (
                      <p className="text-red-600 text-sm mt-2 text-center">{promocodeError}</p>
                    )}
                  </div>

                  {/* Affiliate Section */}
                  <div data-testid="subscription-affiliate-block" className="bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-500 rounded-lg p-4 w-full flex flex-col">
                    <div className="text-center mb-2">
                      <span className="text-lg font-bold">🤝 {t('dashboard.affiliateBlock.title')}</span>
                    </div>
                    <p className="text-sm text-gray-700 text-center mb-3 flex-1">
                      {t('dashboard.affiliateBlock.desc')}
                    </p>
                    <Button
                      data-testid="subscription-affiliate-btn"
                      onClick={() => navigate('/partner/dashboard')}
                      className="bg-black text-white hover:bg-gray-800 font-semibold w-full"
                    >
                      {t('dashboard.affiliateBlock.cta')}
                    </Button>
                  </div>
                </div>

                {/* Pricing Plans — Free / Starter / Professional (same as landing) */}
                <div className="grid gap-6 md:grid-cols-3 max-w-6xl mx-auto">
                  {/* Free Plan */}
                  <div data-testid="sub-plan-free" className={`border-4 rounded-lg p-6 flex flex-col ${
                    !subscriptionInfo?.is_paid ? 'border-green-500 bg-green-50' : 'border-black bg-white'
                  }`}>
                    <div className="text-center">
                      <h3 className="text-2xl font-bold mb-2">{t('landing.plan.free.name')}</h3>
                      <div className="text-4xl font-bold mb-2">
                        {paymentSystem === 'tinkoff' ? '0₽' : '€0'}
                        <span className="text-lg text-gray-600">/{t('landing.price.perMonth')}</span>
                      </div>
                      <p className="text-gray-600 mb-6">{t('landing.plan.free.desc')}</p>
                    </div>
                    <ul className="space-y-3 mb-6 flex-grow">
                      {['f1', 'f2', 'f3', 'f4'].map((k) => (
                        <li key={k} className="flex items-start">
                          <span className="text-green-500 mr-2">✓</span>
                          <span>{t(`landing.plan.free.${k}`)}</span>
                        </li>
                      ))}
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        <span>{t('landing.plan.free.limit')}</span>
                      </li>
                    </ul>
                    <Button
                      disabled
                      className="w-full py-6 text-lg font-bold bg-gray-300 text-gray-600 cursor-not-allowed"
                    >
                      {!subscriptionInfo?.is_paid ? (t('subscription.currentPlan') || 'Текущий план') : '€0'}
                    </Button>
                  </div>

                  {/* Starter Plan */}
                  <div className={`border-4 rounded-lg p-6 flex flex-col ${
                    subscriptionInfo?.plan === 'starter' && subscriptionInfo?.is_paid
                      ? 'border-green-500 bg-green-50' 
                      : 'border-black bg-white'
                  }`}>
                    <div className="text-center">
                      <h3 className="text-2xl font-bold mb-2">{t('landing.plan.starter.name')}</h3>
                      <div className="text-4xl font-bold mb-2">
                        {getPrice('starter').discount > 0 && (
                          <div className="text-xl text-gray-500 line-through mb-1">
                            {getPrice('starter').currencySymbol}{Math.round(PRICING[paymentSystem === 'tinkoff' ? 'rub' : 'eur'][billingPeriod]['starter'])}
                          </div>
                        )}
                        <span className={getPrice('starter').discount > 0 ? 'text-green-600' : ''}>
                          {getPrice('starter').currencySymbol}{Math.round(getPrice('starter').price)}
                        </span>
                        {billingPeriod === 'yearly' && <span className="text-lg text-gray-600">/{t('subscription.year') || 'год'}</span>}
                        {billingPeriod === 'monthly' && <span className="text-lg text-gray-600">/{t('landing.price.perMonth')}</span>}
                      </div>
                      {billingPeriod === 'yearly' && (
                        <div className="text-sm text-green-600 font-semibold mb-2">
                          {t('subscription.save2Months') || 'Экономия 2 месяца!'}
                        </div>
                      )}
                      <p className="text-gray-600 mb-6">{t('subscription.idealForStart')}</p>
                    </div>
                    <ul className="space-y-3 mb-6 flex-grow">
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        <span>{t('landing.price.calendar')}</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        <span className="font-semibold">{t('landing.plan.starter.channels')}</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        <span>100 {t('subscription.conversationsPerMonth')}</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        <span>{t('subscription.aiAssistant')}</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        <span>{t('subscription.emailNotifications')}</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        <span>{t('bookings.storageInfo') || 'История заявок 30 дней'}</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        <span>{t('subscription.knowledgeBase') || 'База знаний для AI'}</span>
                      </li>
                    </ul>
                    <Button
                      onClick={() => handleUpgradePlan('starter')}
                      disabled={isButtonDisabled('starter')}
                      className={`w-full py-6 text-lg font-bold ${
                        isButtonDisabled('starter')
                          ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                          : subscriptionInfo?.plan === 'starter' && billingPeriod === 'yearly'
                          ? 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-black text-white hover:bg-gray-800'
                      }`}
                    >
                      {getButtonText('starter')}
                    </Button>
                  </div>

                  {/* Professional Plan */}
                  <div className={`border-4 rounded-lg p-6 relative flex flex-col ${
                    subscriptionInfo?.plan === 'professional' && subscriptionInfo?.is_paid
                      ? 'border-green-500 bg-green-50' 
                      : 'border-blue-500 bg-blue-50'
                  }`}>
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-bold">
                      {t('subscription.popular')}
                    </div>
                    <div className="text-center mt-2">
                      <h3 className="text-2xl font-bold mb-2">{t('landing.plan.professional.name')}</h3>
                      <div className="text-4xl font-bold mb-2">
                        {getPrice('professional').discount > 0 && (
                          <div className="text-xl text-gray-500 line-through mb-1">
                            {getPrice('professional').currencySymbol}{Math.round(PRICING[paymentSystem === 'tinkoff' ? 'rub' : 'eur'][billingPeriod]['professional'])}
                          </div>
                        )}
                        <span className={getPrice('professional').discount > 0 ? 'text-green-600' : ''}>
                          {getPrice('professional').currencySymbol}{Math.round(getPrice('professional').price)}
                        </span>
                        {billingPeriod === 'yearly' && <span className="text-lg text-gray-600">/{t('subscription.year') || 'год'}</span>}
                        {billingPeriod === 'monthly' && <span className="text-lg text-gray-600">/{t('landing.price.perMonth')}</span>}
                      </div>
                      {billingPeriod === 'yearly' && (
                        <div className="text-sm text-green-600 font-semibold mb-2">
                          {t('subscription.save2Months') || 'Экономия 2 месяца!'}
                        </div>
                      )}
                      <p className="text-gray-600 mb-6">{t('subscription.forGrowingBusiness')}</p>
                    </div>
                    <ul className="space-y-3 mb-6 flex-grow">
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        <span>{t('landing.price.calendar')}</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        <span className="font-semibold">{t('landing.plan.professional.channels')}</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        <span className="font-semibold">1000 {t('subscription.conversationsPerMonth')}</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        <span>{t('subscription.telegramNotifications')}</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        <span className="font-semibold">{t('subscription.analytics')}</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        <span>{t('subscription.utmTracking') || 'UTM отслеживание'}</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        <span>{t('subscription.widgetCustomization')}</span>
                      </li>
                    </ul>
                    <Button
                      onClick={() => handleUpgradePlan('professional')}
                      disabled={isButtonDisabled('professional')}
                      className={`w-full py-6 text-lg font-bold ${
                        isButtonDisabled('professional')
                          ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                          : subscriptionInfo?.plan === 'professional' && billingPeriod === 'yearly'
                          ? 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {getButtonText('professional')}
                    </Button>
                  </div>

                  {/* Unlimited — removed from UI (manual sales only) */}
                </div>

                {/* Payment Info */}
                <div className="mt-8 p-4 bg-gray-100 rounded-lg border border-gray-300">
                  <p className="text-sm text-gray-700">
                    💳 <strong>{t('subscription.securePayment')}:</strong> {t('subscription.securePaymentInfo')}
                  </p>
                  <p className="text-sm text-gray-700 mt-2">
                    🔄 <strong>{t('subscription.cancelAnytime')}:</strong> {t('subscription.cancelInfo')}
                  </p>
                </div>

                {/* Manage subscription via Stripe Customer Portal */}
                {subscriptionInfo?.is_paid && (
                  <div className="mt-4 p-4 bg-white rounded-lg border-2 border-black flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <p className="font-bold">⚙️ {t('subscription.manageBilling') || 'Управление подпиской'}</p>
                      <p className="text-sm text-gray-600">{t('subscription.manageBillingDesc') || 'Отмена подписки, смена карты, скачивание счетов (инвойсов)'}</p>
                    </div>
                    <Button
                      data-testid="manage-billing-portal-btn"
                      onClick={handleManageBilling}
                      className="bg-black text-white hover:bg-gray-800 whitespace-nowrap"
                    >
                      {t('subscription.manageBillingBtn') || 'Открыть портал'}
                    </Button>
                  </div>
                )}
                {subscriptionInfo?.is_paid && subscriptionInfo?.has_stripe_subscription && (
                  <div data-testid="subscription-cancel-block" className="mt-3 p-4 rounded-lg border-2 bg-white border-gray-300 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <p className="font-bold">{t('subscription.cancelTitle')}</p>
                      <p className="text-sm text-gray-600">{t('subscription.cancelDesc')}</p>
                      {subscriptionInfo.last_payment_at && (
                        <p className="text-xs text-gray-500 mt-1" data-testid="subscription-last-payment">
                          {t('subscription.lastPayment')}: {new Date(subscriptionInfo.last_payment_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <Button data-testid="subscription-cancel-btn" variant="outline" onClick={handleCancelSubscription} className="border-2 border-red-300 text-red-700 hover:bg-red-50 whitespace-nowrap">
                      {t('subscription.cancelBtn')}
                    </Button>
                  </div>
                )}

                {/* Payment History */}
                {paymentHistory.length > 0 && (
                  <div className="mt-8">
                    <h3 className="text-xl font-bold mb-4">📜 {t('subscription.paymentHistory') || 'История платежей'}</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full border-2 border-black">
                        <thead className="bg-black text-white">
                          <tr>
                            <th className="px-4 py-3 text-left">{t('subscription.date') || 'Дата'}</th>
                            <th className="px-4 py-3 text-left">{t('subscription.plan') || 'План'}</th>
                            <th className="px-4 py-3 text-left">{t('subscription.amount') || 'Сумма'}</th>
                            <th className="px-4 py-3 text-left">{t('subscription.status') || 'Статус'}</th>
                            <th className="px-4 py-3 text-left">{t('subscription.invoice') || 'Инвойс'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paymentHistory.map((transaction, index) => (
                            <tr key={transaction.id || index} className="border-t-2 border-black">
                              <td className="px-4 py-3">
                                {new Date(transaction.created_at).toLocaleDateString('ru-RU', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </td>
                              <td className="px-4 py-3">
                                <span className="font-semibold uppercase">{transaction.plan}</span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="font-bold">
                                  €{transaction.amount.toFixed(2)}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                {transaction.payment_status === 'paid' ? (
                                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                                    ✅ {t('subscription.paid') || 'Оплачено'}
                                  </span>
                                ) : transaction.payment_status === 'pending' ? (
                                  <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                                    ⏳ {t('subscription.pending') || 'Ожидание'}
                                  </span>
                                ) : (
                                  <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                                    ❌ {t('subscription.failed') || 'Ошибка'}
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                {transaction.payment_status === 'paid' && transaction.invoice_pdf ? (
                                  <a
                                    data-testid={`download-invoice-link-${index}`}
                                    href={transaction.invoice_pdf}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-block px-3 py-1 border-2 border-black rounded-md text-sm font-medium hover:bg-black hover:text-white transition-colors"
                                  >
                                    📄 PDF
                                  </a>
                                ) : transaction.payment_status === 'paid' && transaction.session_id?.startsWith('cs_') ? (
                                  <button
                                    data-testid={`download-invoice-btn-${index}`}
                                    onClick={() => handleDownloadInvoice(transaction.session_id)}
                                    className="px-3 py-1 border-2 border-black rounded-md text-sm font-medium hover:bg-black hover:text-white transition-colors"
                                  >
                                    📄 PDF
                                  </button>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>


          <TabsContent value="widget" className="mt-0">
            <Card className="bg-white text-black border-4 border-black">
              <CardHeader>
                <CardTitle className="text-2xl">{t('widget.title')}</CardTitle>
                <CardDescription className="text-gray-600">
                  {t('widget.subtitle')} &lt;/body&gt;
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Widget Installation Status */}
                <div className="flex items-center justify-between p-4 bg-gray-50 border-2 border-gray-300 rounded-lg">
                  <div className="flex items-center gap-3">
                    {/* Status Indicator */}
                    <div className="text-3xl">
                      {widgetStatus === 'unchecked' && '⚪'}
                      {widgetStatus === 'checking' && '🔄'}
                      {widgetStatus === 'active' && '🟢'}
                      {widgetStatus === 'configured' && '🟡'}
                      {widgetStatus === 'inactive' && '🔴'}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">
                        {t('widget.status.title')}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {widgetStatus === 'unchecked' && t('widget.status.unchecked')}
                        {widgetStatus === 'checking' && t('widget.status.checking')}
                        {widgetStatus === 'active' && t('widget.status.active').replace('{time}', lastActivityTime)}
                        {widgetStatus === 'configured' && t('widget.status.configured')}
                        {widgetStatus === 'inactive' && t('widget.status.inactive')}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={checkWidgetInstallation}
                    disabled={widgetStatus === 'checking'}
                    className="bg-blue-600 text-white hover:bg-blue-700"
                  >
                    {widgetStatus === 'checking' ? t('widget.status.checking') : t('widget.status.checkButton')}
                  </Button>
                </div>
                
                <div className="bg-gray-100 p-4 rounded-lg border-2 border-black font-mono text-xs overflow-x-auto">
                  <pre data-testid="widget-code-display">{getWidgetCode()}</pre>
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid="copy-widget-code-button"
                    onClick={copyWidgetCode}
                    className="flex-1 bg-black text-white hover:bg-gray-800"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    {t('widget.copy')}
                  </Button>
                  <Button
                    data-testid="open-demo-button"
                    onClick={() => window.open(`/widget-demo/${salon?.id}`, '_blank')}
                    className="flex-1 border-2 border-black hover:bg-gray-100"
                    variant="outline"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {t('widget.demo')}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Integrations Section */}
            <Card className="bg-white text-black border-4 border-black mt-6">
              <CardHeader>
                <CardTitle className="text-2xl">🔌 {t('integrations.title')}</CardTitle>
                <CardDescription className="text-gray-600">
                  {t('integrations.subtitle')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                
                {/* Direct booking link + QR code — first in integrations */}
                {!isEcom && (
                <div className="mb-6">
                  <ShortLinkBlock API={API} />
                </div>
                )}

                {/* Integration category tiles */}
                <div id="integrations-block" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6 scroll-mt-4" data-testid="integration-categories">
                  {[
                    { key: 'website', icon: '🌐', label: t('integrations.cat.website'), desc: t('integrations.cat.websiteDesc') },
                    { key: 'whatsapp', icon: '📲', label: 'WhatsApp', desc: t('integrations.cat.whatsappDesc') },
                    { key: 'instagram', icon: '📸', label: 'Instagram', desc: t('integrations.cat.instagramDesc') },
                    { key: 'ident', icon: '🦷', label: 'IDENT', desc: t('integrations.cat.identDesc') },
                    { key: 'vk', icon: '💬', label: t('integrations.cat.vk'), desc: t('integrations.cat.vkDesc') },
                    { key: 'voice', icon: '📞', label: t('integrations.cat.voice'), desc: t('integrations.cat.voiceDesc') },
                    { key: 'altegio', icon: '📒', label: 'Altegio', desc: t('integrations.cat.altegioDesc') },
                  ].filter((c) => !(isEcom && (c.key === 'ident' || c.key === 'altegio'))).map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      data-testid={`integration-cat-${c.key}`}
                      onClick={() => setIntegrationCategory(integrationCategory === c.key ? null : c.key)}
                      className={`text-left border-2 rounded-lg p-4 transition-colors ${
                        integrationCategory === c.key ? 'border-black bg-black text-white' : 'border-black bg-white hover:bg-gray-50'
                      }`}
                    >
                      <div className="text-3xl mb-2">{c.icon}</div>
                      <div className="font-bold text-sm sm:text-base">{c.label}</div>
                      <div className={`text-xs mt-1 ${integrationCategory === c.key ? 'text-gray-300' : 'text-gray-500'}`}>{c.desc}</div>
                    </button>
                  ))}
                </div>

                {integrationCategory === 'website' && (<div data-testid="integrations-website-group">
                {/* WordPress Integration - Accordion */}
                <div className="border-2 border-black rounded-lg overflow-hidden">
                  {/* Accordion Header */}
                  <button
                    onClick={() => setWordpressAccordionOpen(!wordpressAccordionOpen)}
                    className="w-full flex items-center justify-between p-4 sm:p-6 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 sm:gap-4">
                      {/* WordPress Logo */}
                      <svg className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0" viewBox="0 0 122.52 122.523" fill="#21759b">
                        <path d="M8.708 61.26c0 20.802 12.089 38.779 29.619 47.298L13.258 39.872a52.354 52.354 0 0 0-4.55 21.388z"/>
                        <path d="M96.74 58.608c0-6.495-2.333-10.993-4.334-14.494-2.664-4.329-5.161-7.995-5.161-12.324 0-4.831 3.664-9.328 8.825-9.328.233 0 .454.029.681.042-9.35-8.566-21.807-13.796-35.489-13.796-18.36 0-34.513 9.42-43.91 23.688 1.233.037 2.395.063 3.382.063 5.497 0 14.006-.667 14.006-.667 2.833-.167 3.167 3.994.337 4.329 0 0-2.847.335-6.015.501L48.2 93.547l11.501-34.493-8.188-22.434c-2.83-.166-5.511-.501-5.511-.501-2.832-.166-2.5-4.496.332-4.329 0 0 8.679.667 13.843.667 5.496 0 14.006-.667 14.006-.667 2.835-.167 3.168 3.994.337 4.329 0 0-2.853.335-6.015.501l18.992 56.494 5.242-17.517c2.272-7.269 4.001-12.49 4.001-16.989z"/>
                        <path d="M62.184 65.857l-15.768 45.819a52.513 52.513 0 0 0 14.846 2.14c6.12 0 11.989-1.058 17.452-2.979a4.405 4.405 0 0 1-.374-.724L62.184 65.857z"/>
                        <path d="M107.376 36.046c.226 1.674.354 3.471.354 5.404 0 5.333-.996 11.328-3.996 18.824l-16.053 46.413c15.624-9.111 26.133-26.038 26.133-45.426.001-9.137-2.333-17.729-6.438-25.215z"/>
                        <path d="M61.262 0C27.483 0 0 27.481 0 61.26c0 33.783 27.483 61.263 61.262 61.263 33.778 0 61.265-27.48 61.265-61.263C122.526 27.481 95.04 0 61.262 0zm0 119.715c-32.23 0-58.453-26.222-58.453-58.455 0-32.23 26.222-58.451 58.453-58.451 32.229 0 58.45 26.221 58.45 58.451 0 32.232-26.221 58.455-58.45 58.455z"/>
                      </svg>
                      <div className="text-left flex-1 min-w-0">
                        <h3 className="text-lg sm:text-xl font-bold">{t('integrations.wordpress.title')}</h3>
                        <p className="text-gray-600 text-xs sm:text-sm mt-1 line-clamp-2">
                          {t('integrations.wordpress.description')}
                        </p>
                      </div>
                    </div>
                    <svg 
                      className={`w-5 h-5 sm:w-6 sm:h-6 transition-transform flex-shrink-0 ml-2 ${wordpressAccordionOpen ? 'rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Accordion Content */}
                  {wordpressAccordionOpen && (
                    <div className="border-t-2 border-black p-4 sm:p-6 bg-gray-50">
                      {/* Widget Key Display */}
                      <div className="bg-white border-2 border-gray-300 rounded-lg p-3 sm:p-4 mb-4">
                        <Label className="font-medium mb-2 block text-sm sm:text-base">{t('integrations.yourWidgetKey')}:</Label>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <code className="flex-1 bg-gray-50 border border-gray-300 rounded px-3 py-2 font-mono text-xs sm:text-sm break-all">
                            {salon?.id || 'Loading...'}
                          </code>
                          <Button
                            variant="outline"
                            onClick={() => {
                              navigator.clipboard.writeText(salon?.id || '');
                              toast.success(t('integrations.keyCopied'));
                            }}
                            className="border-2 border-black hover:bg-black hover:text-white h-10 px-4 whitespace-nowrap"
                          >
                            📋 {t('integrations.copyKey')}
                          </Button>
                        </div>
                      </div>
                      
                      {/* Download Plugin & Send to Developer */}
                      <div className="flex flex-col gap-3">
                        <a 
                          href="/chatity-ai-chat-widget.zip" 
                          download
                          className="w-full inline-flex items-center justify-center h-10 px-4 bg-black text-white font-bold rounded-lg hover:bg-gray-800 transition-colors text-sm sm:text-base"
                        >
                          ⬇️ {t('integrations.wordpress.download')}
                        </a>
                        <Button
                          variant="outline"
                          onClick={() => setShowDeveloperEmailModal(true)}
                          className="w-full border-2 border-black hover:bg-gray-100 h-10 text-sm sm:text-base"
                        >
                          📧 {t('integrations.sendToDeveloper')}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* HTML/JavaScript Integration */}
                <div className="border-2 border-black rounded-lg overflow-hidden mt-6">
                  <button
                    onClick={() => setHtmlAccordionOpen(!htmlAccordionOpen)}
                    className="w-full flex items-center justify-between p-4 sm:p-6 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-orange-500 rounded-lg flex-shrink-0">
                        <span className="text-2xl">🌐</span>
                      </div>
                      <div className="text-left">
                        <h3 className="text-lg sm:text-xl font-bold">{t('integrations.html.title')}</h3>
                        <p className="text-xs sm:text-sm text-gray-600">{t('integrations.html.description')}</p>
                      </div>
                    </div>
                    <svg
                      className={`w-5 h-5 sm:w-6 sm:h-6 transition-transform flex-shrink-0 ml-2 ${htmlAccordionOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {htmlAccordionOpen && (
                    <div className="border-t-2 border-black p-4 sm:p-6 bg-gray-50">
                      <div className="bg-white border-2 border-gray-300 rounded-lg p-3 sm:p-4 mb-4">
                        <Label className="font-medium mb-2 block text-sm sm:text-base">{t('integrations.yourWidgetKey')}:</Label>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <code className="flex-1 bg-gray-50 border border-gray-300 rounded px-3 py-2 font-mono text-xs sm:text-sm break-all">
                            {salon?.id || 'Loading...'}
                          </code>
                          <Button
                            variant="outline"
                            onClick={() => {
                              navigator.clipboard.writeText(salon?.id || '');
                              toast.success(t('integrations.keyCopied'));
                            }}
                            className="border-2 border-black hover:bg-black hover:text-white h-10 px-4 whitespace-nowrap"
                          >
                            📋 {t('integrations.copyKey')}
                          </Button>
                        </div>
                      </div>
                      
                      <div className="bg-gray-900 text-green-400 rounded-lg p-3 sm:p-4 font-mono text-xs sm:text-sm overflow-x-auto mb-3">
                        <pre className="whitespace-pre-wrap break-all">{'<script src="https://chatity.io/api/widget/embed.js?id=' + (salon?.id || 'YOUR_WIDGET_KEY') + '"></script>'}</pre>
                      </div>
                      
                      <div className="prose prose-sm max-w-none mb-4">
                        <p className="text-sm mb-2">{t('integrations.html.step1')}</p>
                        <p className="text-sm">{t('integrations.html.step2')}</p>
                      </div>
                      
                      <Button
                        variant="outline"
                        onClick={() => {
                          const code = `<script src="https://chatity.io/api/widget/embed.js?id=${salon?.id || 'YOUR_WIDGET_KEY'}"></script>`;
                          navigator.clipboard.writeText(code);
                          toast.success(t('widget.copied'));
                        }}
                        className="w-full border-2 border-black hover:bg-black hover:text-white h-10"
                      >
                        📋 {t('widget.copy')}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Webflow Integration */}
                <div className="border-2 border-black rounded-lg overflow-hidden mt-6">
                  <button
                    onClick={() => setWebflowAccordionOpen(!webflowAccordionOpen)}
                    className="w-full flex items-center justify-between p-4 sm:p-6 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-blue-600 rounded-lg flex-shrink-0">
                        <span className="text-white text-2xl font-bold">W</span>
                      </div>
                      <div className="text-left">
                        <h3 className="text-lg sm:text-xl font-bold">Webflow</h3>
                        <p className="text-xs sm:text-sm text-gray-600">{t('integrations.webflow.description')}</p>
                      </div>
                    </div>
                    <svg
                      className={`w-5 h-5 sm:w-6 sm:h-6 transition-transform flex-shrink-0 ml-2 ${webflowAccordionOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {webflowAccordionOpen && (
                    <div className="border-t-2 border-black p-4 sm:p-6 bg-gray-50">
                      <div className="prose prose-sm max-w-none mb-4">
                        <ol className="list-decimal pl-5 space-y-2">
                          <li>{t('integrations.webflow.step1')}</li>
                          <li>{t('integrations.webflow.step2')}</li>
                          <li>{t('integrations.webflow.step3')}</li>
                          <li>{t('integrations.webflow.step4')}</li>
                        </ol>
                      </div>
                      
                      <div className="bg-gray-900 text-green-400 rounded-lg p-3 sm:p-4 font-mono text-xs sm:text-sm overflow-x-auto mb-3">
                        <pre className="whitespace-pre-wrap break-all">{'<script src="https://chatity.io/api/widget/embed.js?id=' + (salon?.id || 'YOUR_WIDGET_KEY') + '"></script>'}</pre>
                      </div>
                      
                      <Button
                        variant="outline"
                        onClick={() => {
                          const code = `<script src="https://chatity.io/api/widget/embed.js?id=${salon?.id || 'YOUR_WIDGET_KEY'}"></script>`;
                          navigator.clipboard.writeText(code);
                          toast.success(t('widget.copied'));
                        }}
                        className="w-full border-2 border-black hover:bg-black hover:text-white h-10"
                      >
                        📋 {t('widget.copy')}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Shopify Integration */}
                <div className="border-2 border-black rounded-lg overflow-hidden mt-6">
                  <button
                    onClick={() => setShopifyAccordionOpen(!shopifyAccordionOpen)}
                    className="w-full flex items-center justify-between p-4 sm:p-6 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-green-600 rounded-lg flex-shrink-0">
                        <span className="text-white text-2xl font-bold">S</span>
                      </div>
                      <div className="text-left">
                        <h3 className="text-lg sm:text-xl font-bold">Shopify</h3>
                        <p className="text-xs sm:text-sm text-gray-600">{t('integrations.shopify.description')}</p>
                      </div>
                    </div>
                    <svg
                      className={`w-5 h-5 sm:w-6 sm:h-6 transition-transform flex-shrink-0 ml-2 ${shopifyAccordionOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {shopifyAccordionOpen && (
                    <div className="border-t-2 border-black p-4 sm:p-6 bg-gray-50">
                      <div className="prose prose-sm max-w-none mb-4">
                        <ol className="list-decimal pl-5 space-y-2">
                          <li>{t('integrations.shopify.step1')}</li>
                          <li>{t('integrations.shopify.step2')}</li>
                          <li>{t('integrations.shopify.step3')}</li>
                          <li>{t('integrations.shopify.step4')}</li>
                          <li>{t('integrations.shopify.step5')}</li>
                        </ol>
                      </div>
                      
                      <div className="bg-gray-900 text-green-400 rounded-lg p-3 sm:p-4 font-mono text-xs sm:text-sm overflow-x-auto mb-3">
                        <pre className="whitespace-pre-wrap break-all">{'<script src="https://chatity.io/api/widget/embed.js?id=' + (salon?.id || 'YOUR_WIDGET_KEY') + '"></script>'}</pre>
                      </div>
                      
                      <Button
                        variant="outline"
                        onClick={() => {
                          const code = `<script src="https://chatity.io/api/widget/embed.js?id=${salon?.id || 'YOUR_WIDGET_KEY'}"></script>`;
                          navigator.clipboard.writeText(code);
                          toast.success(t('widget.copied'));
                        }}
                        className="w-full border-2 border-black hover:bg-black hover:text-white h-10"
                      >
                        📋 {t('widget.copy')}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Wix Integration */}
                <div className="border-2 border-black rounded-lg overflow-hidden mt-6">
                  <button
                    onClick={() => setWixAccordionOpen(!wixAccordionOpen)}
                    className="w-full flex items-center justify-between p-4 sm:p-6 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-black rounded-lg flex-shrink-0">
                        <span className="text-white text-2xl font-bold">W</span>
                      </div>
                      <div className="text-left">
                        <h3 className="text-lg sm:text-xl font-bold">Wix</h3>
                        <p className="text-xs sm:text-sm text-gray-600">{t('integrations.wix.description')}</p>
                      </div>
                    </div>
                    <svg
                      className={`w-5 h-5 sm:w-6 sm:h-6 transition-transform flex-shrink-0 ml-2 ${wixAccordionOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {wixAccordionOpen && (
                    <div className="border-t-2 border-black p-4 sm:p-6 bg-gray-50">
                      <div className="prose prose-sm max-w-none mb-4">
                        <ol className="list-decimal pl-5 space-y-2">
                          <li>{t('integrations.wix.step1')}</li>
                          <li>{t('integrations.wix.step2')}</li>
                          <li>{t('integrations.wix.step3')}</li>
                          <li>{t('integrations.wix.step4')}</li>
                        </ol>
                      </div>
                      
                      <div className="bg-gray-900 text-green-400 rounded-lg p-3 sm:p-4 font-mono text-xs sm:text-sm overflow-x-auto mb-3">
                        <pre className="whitespace-pre-wrap break-all">{'<script src="https://chatity.io/api/widget/embed.js?id=' + (salon?.id || 'YOUR_WIDGET_KEY') + '"></script>'}</pre>
                      </div>
                      
                      <Button
                        variant="outline"
                        onClick={() => {
                          const code = `<script src="https://chatity.io/api/widget/embed.js?id=${salon?.id || 'YOUR_WIDGET_KEY'}"></script>`;
                          navigator.clipboard.writeText(code);
                          toast.success(t('widget.copied'));
                        }}
                        className="w-full border-2 border-black hover:bg-black hover:text-white h-10"
                      >
                        📋 {t('widget.copy')}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Tilda Integration */}
                <div className="border-2 border-black rounded-lg overflow-hidden mt-6">
                  <button
                    onClick={() => setTildaAccordionOpen(!tildaAccordionOpen)}
                    className="w-full flex items-center justify-between p-4 sm:p-6 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-orange-600 rounded-lg flex-shrink-0">
                        <span className="text-white text-2xl font-bold">T</span>
                      </div>
                      <div className="text-left">
                        <h3 className="text-lg sm:text-xl font-bold">Tilda</h3>
                        <p className="text-xs sm:text-sm text-gray-600">{t('integrations.tilda.description')}</p>
                      </div>
                    </div>
                    <svg
                      className={`w-5 h-5 sm:w-6 sm:h-6 transition-transform flex-shrink-0 ml-2 ${tildaAccordionOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {tildaAccordionOpen && (
                    <div className="border-t-2 border-black p-4 sm:p-6 bg-gray-50">
                      <div className="prose prose-sm max-w-none mb-4">
                        <ol className="list-decimal pl-5 space-y-2">
                          <li>{t('integrations.tilda.step1')}</li>
                          <li>{t('integrations.tilda.step2')}</li>
                          <li>{t('integrations.tilda.step3')}</li>
                          <li>{t('integrations.tilda.step4')}</li>
                        </ol>
                      </div>
                      
                      <div className="bg-gray-900 text-green-400 rounded-lg p-3 sm:p-4 font-mono text-xs sm:text-sm overflow-x-auto mb-3">
                        <pre className="whitespace-pre-wrap break-all">{'<script src="https://chatity.io/api/widget/embed.js?id=' + (salon?.id || 'YOUR_WIDGET_KEY') + '"></script>'}</pre>
                      </div>
                      
                      <Button
                        variant="outline"
                        onClick={() => {
                          const code = `<script src="https://chatity.io/api/widget/embed.js?id=${salon?.id || 'YOUR_WIDGET_KEY'}"></script>`;
                          navigator.clipboard.writeText(code);
                          toast.success(t('widget.copied'));
                        }}
                        className="w-full border-2 border-black hover:bg-black hover:text-white h-10"
                      >
                        📋 {t('widget.copy')}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Squarespace Integration */}
                <div className="border-2 border-black rounded-lg overflow-hidden mt-6">
                  <button
                    onClick={() => setSquarespaceAccordionOpen(!squarespaceAccordionOpen)}
                    className="w-full flex items-center justify-between p-4 sm:p-6 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-black rounded-lg flex-shrink-0">
                        <span className="text-white text-2xl font-bold">□</span>
                      </div>
                      <div className="text-left">
                        <h3 className="text-lg sm:text-xl font-bold">Squarespace</h3>
                        <p className="text-xs sm:text-sm text-gray-600">{t('integrations.squarespace.description')}</p>
                      </div>
                    </div>
                    <svg
                      className={`w-5 h-5 sm:w-6 sm:h-6 transition-transform flex-shrink-0 ml-2 ${squarespaceAccordionOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {squarespaceAccordionOpen && (
                    <div className="border-t-2 border-black p-4 sm:p-6 bg-gray-50">
                      <div className="prose prose-sm max-w-none mb-4">
                        <ol className="list-decimal pl-5 space-y-2">
                          <li>{t('integrations.squarespace.step1')}</li>
                          <li>{t('integrations.squarespace.step2')}</li>
                          <li>{t('integrations.squarespace.step3')}</li>
                          <li>{t('integrations.squarespace.step4')}</li>
                        </ol>
                      </div>
                      
                      <div className="bg-gray-900 text-green-400 rounded-lg p-3 sm:p-4 font-mono text-xs sm:text-sm overflow-x-auto mb-3">
                        <pre className="whitespace-pre-wrap break-all">{'<script src="https://chatity.io/api/widget/embed.js?id=' + (salon?.id || 'YOUR_WIDGET_KEY') + '"></script>'}</pre>
                      </div>
                      
                      <Button
                        variant="outline"
                        onClick={() => {
                          const code = `<script src="https://chatity.io/api/widget/embed.js?id=${salon?.id || 'YOUR_WIDGET_KEY'}"></script>`;
                          navigator.clipboard.writeText(code);
                          toast.success(t('widget.copied'));
                        }}
                        className="w-full border-2 border-black hover:bg-black hover:text-white h-10"
                      >
                        📋 {t('widget.copy')}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Google Tag Manager Integration */}
                <div className="border-2 border-black rounded-lg overflow-hidden mt-6">
                  <button
                    onClick={() => setGtmAccordionOpen(!gtmAccordionOpen)}
                    className="w-full flex items-center justify-between p-4 sm:p-6 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-blue-500 rounded-lg flex-shrink-0">
                        <span className="text-white text-xl font-bold">GTM</span>
                      </div>
                      <div className="text-left">
                        <h3 className="text-lg sm:text-xl font-bold">Google Tag Manager</h3>
                        <p className="text-xs sm:text-sm text-gray-600">{t('integrations.gtm.description')}</p>
                      </div>
                    </div>
                    <svg
                      className={`w-5 h-5 sm:w-6 sm:h-6 transition-transform flex-shrink-0 ml-2 ${gtmAccordionOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {gtmAccordionOpen && (
                    <div className="border-t-2 border-black p-4 sm:p-6 bg-gray-50">
                      <div className="prose prose-sm max-w-none mb-4">
                        <ol className="list-decimal pl-5 space-y-2">
                          <li>{t('integrations.gtm.step1')}</li>
                          <li>{t('integrations.gtm.step2')}</li>
                          <li>{t('integrations.gtm.step3')}</li>
                          <li>{t('integrations.gtm.step4')}</li>
                          <li>{t('integrations.gtm.step5')}</li>
                        </ol>
                      </div>
                      
                      <div className="bg-gray-900 text-green-400 rounded-lg p-3 sm:p-4 font-mono text-xs sm:text-sm overflow-x-auto mb-3">
                        <pre className="whitespace-pre-wrap break-all">{'<script src="https://chatity.io/api/widget/embed.js?id=' + (salon?.id || 'YOUR_WIDGET_KEY') + '"></script>'}</pre>
                      </div>
                      
                      <Button
                        variant="outline"
                        onClick={() => {
                          const code = `<script src="https://chatity.io/api/widget/embed.js?id=${salon?.id || 'YOUR_WIDGET_KEY'}"></script>`;
                          navigator.clipboard.writeText(code);
                          toast.success(t('widget.copied'));
                        }}
                        className="w-full border-2 border-black hover:bg-black hover:text-white h-10"
                      >
                        📋 {t('widget.copy')}
                      </Button>
                    </div>
                  )}
                </div>
                </div>)}

                {/* Twilio Voice AI Integration */}
                {integrationCategory === 'voice' && <PhoneAgentCard />}
                {integrationCategory === 'altegio' && (<div className="mt-6" data-testid="integrations-altegio-group"><AltegioConnect API={API} /></div>)}
                {false && integrationCategory === 'voice' && (
                <div className="border-2 border-black rounded-lg overflow-hidden mt-4">
                  <button
                    onClick={() => setTwilioAccordionOpen(!twilioAccordionOpen)}
                    className="w-full flex items-center justify-between p-4 sm:p-6 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-red-600 rounded-lg flex-shrink-0">
                        <span className="text-white text-2xl">📞</span>
                      </div>
                      <div className="text-left flex-1 min-w-0">
                        <h3 className="text-lg sm:text-xl font-bold">{t('integrations.twilio.title')}</h3>
                        <p className="text-gray-600 text-xs sm:text-sm mt-1">
                          {t('integrations.twilio.description')}
                        </p>
                        <div className="mt-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                            twilioStatus.enabled 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {twilioStatus.enabled ? '🟢 ' + t('integrations.twilio.enabled') : '⚪ ' + t('integrations.twilio.disabled')}
                          </span>
                        </div>
                      </div>
                    </div>
                    <svg 
                      className={`w-5 h-5 sm:w-6 sm:h-6 transition-transform flex-shrink-0 ml-2 ${twilioAccordionOpen ? 'rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {twilioAccordionOpen && (
                    <div className="border-t-2 border-black p-4 sm:p-6 bg-gray-50">
                      
                      {/* Credentials Input Form */}
                      {!twilioStatus.enabled && (
                        <div className="bg-white border-2 border-gray-300 rounded-lg p-4 mb-4">
                          <h4 className="font-bold mb-3 text-sm sm:text-base">
                            {t('integrations.twilio.credentials') || 'Twilio Credentials'}
                          </h4>
                          <p className="text-xs text-gray-600 mb-4">
                            {t('integrations.twilio.credentialsInfo') || 'Enter your Twilio credentials. You can find them in your Twilio Console.'}
                          </p>
                          
                          <div className="space-y-3">
                            <div>
                              <Label className="font-medium mb-1 block text-sm">
                                Account SID:
                              </Label>
                              <input
                                type="text"
                                value={twilioCredentials.account_sid}
                                onChange={(e) => setTwilioCredentials({...twilioCredentials, account_sid: e.target.value})}
                                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                                className="w-full border-2 border-gray-300 rounded px-3 py-2 text-sm focus:border-black focus:outline-none"
                              />
                            </div>
                            
                            <div>
                              <Label className="font-medium mb-1 block text-sm">
                                Auth Token:
                              </Label>
                              <input
                                type="password"
                                value={twilioCredentials.auth_token}
                                onChange={(e) => setTwilioCredentials({...twilioCredentials, auth_token: e.target.value})}
                                placeholder="********************************"
                                className="w-full border-2 border-gray-300 rounded px-3 py-2 text-sm focus:border-black focus:outline-none"
                              />
                            </div>
                            
                            <div>
                              <Label className="font-medium mb-1 block text-sm">
                                {t('integrations.twilio.phoneNumber')}:
                              </Label>
                              <input
                                type="tel"
                                value={twilioCredentials.phone_number}
                                onChange={(e) => setTwilioCredentials({...twilioCredentials, phone_number: e.target.value})}
                                placeholder="+1234567890"
                                className="w-full border-2 border-gray-300 rounded px-3 py-2 text-sm focus:border-black focus:outline-none"
                              />
                            </div>
                            
                            <div>
                              <Label className="font-medium mb-1 block text-sm">
                                {t('integrations.twilio.voiceLanguage') || 'Язык голоса'}:
                              </Label>
                              <select
                                value={twilioCredentials.voice_language}
                                onChange={(e) => setTwilioCredentials({...twilioCredentials, voice_language: e.target.value})}
                                className="w-full border-2 border-gray-300 rounded px-3 py-2 text-sm focus:border-black focus:outline-none"
                              >
                                <option value="ru-RU">🇷🇺 Русский (Russian)</option>
                                <option value="en-US">🇺🇸 English (US)</option>
                                <option value="en-GB">🇬🇧 English (UK)</option>
                                <option value="fr-FR">🇫🇷 Français (French)</option>
                                <option value="de-DE">🇩🇪 Deutsch (German)</option>
                                <option value="es-ES">🇪🇸 Español (Spanish)</option>
                                <option value="it-IT">🇮🇹 Italiano (Italian)</option>
                                <option value="pt-BR">🇧🇷 Português (Portuguese)</option>
                                <option value="ja-JP">🇯🇵 日本語 (Japanese)</option>
                                <option value="ko-KR">🇰🇷 한국어 (Korean)</option>
                                <option value="zh-CN">🇨🇳 中文 (Chinese)</option>
                              </select>
                            </div>
                            
                            <div>
                              <Label className="font-medium mb-1 block text-sm">
                                {t('integrations.twilio.voiceSpeed') || 'Скорость речи'}:
                              </Label>
                              <select
                                value={twilioCredentials.voice_rate}
                                onChange={(e) => setTwilioCredentials({...twilioCredentials, voice_rate: e.target.value})}
                                className="w-full border-2 border-gray-300 rounded px-3 py-2 text-sm focus:border-black focus:outline-none"
                              >
                                <option value="75">🐢 Медленно (75%)</option>
                                <option value="85">🐌 Чуть медленнее (85%)</option>
                                <option value="100">⚡ Нормально (100%)</option>
                                <option value="115">🚀 Быстрее (115%)</option>
                                <option value="130">⚡⚡ Очень быстро (130%)</option>
                              </select>
                            </div>
                          </div>
                          
                          <Button
                            onClick={handleEnableTwilio}
                            disabled={twilioLoading}
                            className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white font-bold h-10"
                          >
                            {twilioLoading ? '⏳ ' + (t('common.loading') || 'Loading...') : '✅ ' + t('integrations.twilio.enable')}
                          </Button>
                        </div>
                      )}

                      {/* Status and Controls (when enabled) */}
                      {twilioStatus.enabled && (
                        <div className="bg-white border-2 border-gray-300 rounded-lg p-4 mb-4">
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <Label className="font-medium block text-sm sm:text-base mb-1">
                                {t('integrations.twilio.status')}:
                              </Label>
                              <span className="text-lg font-bold text-green-600">
                                ✅ {t('integrations.twilio.enabled')}
                              </span>
                            </div>
                            <Button
                              onClick={handleDisableTwilio}
                              disabled={twilioLoading}
                              className="bg-red-600 hover:bg-red-700 text-white font-bold h-10 px-6"
                            >
                              {twilioLoading ? '⏳' : t('integrations.twilio.disable')}
                            </Button>
                          </div>

                          {twilioStatus.phone_number && (
                          <>
                            <div className="mb-3">
                              <Label className="font-medium mb-2 block text-sm">
                                {t('integrations.twilio.phoneNumber')}:
                              </Label>
                              <code className="block bg-gray-100 border border-gray-300 rounded px-3 py-2 text-sm font-mono">
                                {twilioStatus.phone_number}
                              </code>
                            </div>

                            <div>
                              <Label className="font-medium mb-2 block text-sm">
                                {t('integrations.twilio.webhookUrl')}:
                              </Label>
                              <div className="flex gap-2">
                                <code className="flex-1 bg-gray-100 border border-gray-300 rounded px-3 py-2 text-xs font-mono break-all">
                                  {twilioStatus.webhook_url}
                                </code>
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    navigator.clipboard.writeText(twilioStatus.webhook_url || '');
                                    toast.success(t('integrations.keyCopied'));
                                  }}
                                  className="border-2 border-black hover:bg-black hover:text-white h-auto px-3 whitespace-nowrap"
                                >
                                  📋
                                </Button>
                              </div>
                              <p className="text-xs text-gray-600 mt-2">
                                {t('integrations.twilio.instructions')}
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                      )}

                      {/* How it works */}
                      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                        <h4 className="font-bold mb-3 text-sm sm:text-base">
                          {t('integrations.twilio.howItWorks')}
                        </h4>
                        <ul className="space-y-2 text-xs sm:text-sm">
                          <li className="flex items-start gap-2">
                            <span>📞</span>
                            <span>{t('integrations.twilio.step1')}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span>🤖</span>
                            <span>{t('integrations.twilio.step2')}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span>📝</span>
                            <span>{t('integrations.twilio.step3')}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span>✅</span>
                            <span>{t('integrations.twilio.step4')}</span>
                          </li>
                        </ul>
                      </div>

                    </div>
                  )}
                </div>
                )}

                {/* IDENT (dent-it.ru) — pull-based ticket export for dental clinics */}
                {integrationCategory === 'ident' && (
                <div className="mt-6">
                  <IdentIntegrationBlock API={API} />
                </div>
                )}

                {/* VK (VKontakte) — AI answers in community DMs, leads → email/telegram */}
                {integrationCategory === 'vk' && (
                <div className="mt-6">
                  <VkIntegrationBlock API={API} />
                </div>
                )}

                {/* WhatsApp via QR (Green API) */}
                {integrationCategory === 'whatsapp' && (
                <div className="mt-6">
                  <GreenApiConnect />
                </div>
                )}

                {/* Instagram (via Zernio — instant connect, no Meta review) */}
                {integrationCategory === 'instagram' && (
                <div className="mt-6" data-testid="integrations-instagram-group">
                  <ZernioInstagram API={API} />
                </div>
                )}


              </CardContent>
            </Card>
          </TabsContent>

          {/* Developer Email Modal */}
          {showDeveloperEmailModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg p-6 max-w-md w-full border-4 border-black relative">
                {/* Close button */}
                <button
                  onClick={() => {
                    setShowDeveloperEmailModal(false);
                    setDeveloperEmail('');
                  }}
                  className="absolute top-4 right-4 text-gray-500 hover:text-black transition-colors"
                  disabled={sendingEmail}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <h3 className="text-xl font-bold mb-4">📧 {t('integrations.sendToDeveloper')}</h3>
                <p className="text-gray-600 mb-4 text-sm">
                  {t('integrations.subtitle')}
                </p>
                
                <Label className="font-medium mb-2 block">{t('integrations.developerEmail')}:</Label>
                <input
                  type="email"
                  value={developerEmail}
                  onChange={(e) => setDeveloperEmail(e.target.value)}
                  placeholder={t('integrations.developerEmailPlaceholder')}
                  className="w-full border-2 border-gray-300 rounded px-3 py-2 mb-4 focus:border-black focus:outline-none"
                />
                
                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      setShowDeveloperEmailModal(false);
                      setDeveloperEmail('');
                    }}
                    variant="outline"
                    className="flex-1 border-2 border-gray-300 hover:bg-gray-100 text-black"
                    disabled={sendingEmail}
                  >
                    {t('support.cancel') || 'Cancel'}
                  </Button>
                  <Button
                    onClick={handleSendToDeveloper}
                    className="flex-1 bg-black text-white hover:bg-gray-800"
                    disabled={sendingEmail}
                  >
                    {sendingEmail ? t('common.sending') || 'Sending...' : t('integrations.send')}
                  </Button>
                </div>
              </div>
            </div>
          )}



          {/* Support Tickets Tab */}
          {/* Mini-site Builder Tab */}
          <TabsContent value="minisite" className="mt-0">
            <MiniSiteBuilder API={API} salon={salon} subscriptionInfo={subscriptionInfo} />
          </TabsContent>

          <TabsContent value="support" className="mt-0">
            <SupportTickets 
              tickets={tickets}
              onTicketsUpdate={fetchTickets}
            />
          </TabsContent>


        </Tabs>
      </main>
      <Footer />
    </div>
  );
}